import {
  Vector3,
  Camera,
  ArcRotateCamera,
  Animation,
  Mesh,
  Color3,
  PointerEventTypes,
  MeshBuilder,
  FreeCamera,
  HemisphericLight,
  PointLight,
  SpotLight,
  DirectionalLight,
  Light,
  RenderTargetTexture,
  Color4,
  StandardMaterial,
  TransformNode,
  Matrix,
  Quaternion,
} from '@babylonjs/core';
import { SceneManager } from './core/SceneManager';
import { EntityRegistry } from './core/EntityRegistry';
import { EntityType } from './types/EntityTypes';
import { EventBus } from './core/EventBus';
import { DebugRenderer } from './core/DebugRenderer';
import { logger } from './core/Logger';
import { gameTime } from './core/GameTime';
import { registerBuiltInBehaviors } from './behaviors';
import { InputManager } from './InputManager';
import { SplineLoader } from './systems/SplineLoader';
import { SplineConstraint } from './systems/SplineConstraint';
import { TriggerSystem } from './systems/TriggerSystem';
import { ServiceRegistry } from './core/ServiceRegistry';
import { CameraController } from './systems/CameraController';
import { PhysicsManager } from './systems/physics';
import { DynamicObjectSystem } from './systems/DynamicObjectSystem';
import { PostProcessManager } from './systems/PostProcessManager';
import { ParticleManager } from './systems/ParticleManager';
import { ImpactEffectManager, ImpactPresets } from './systems/effects';
import { FXManager } from './systems/effects/FXManager';
import { LevelBuilder } from './systems/LevelBuilder';
import { UnifiedLightManager } from './systems/UnifiedLightManager';
import { EnvironmentSettings } from './systems/EnvironmentSettings';
import { PartyManager } from './systems/PartyManager';
import { NPCSpawnManager } from './systems/NPCSpawnManager';
import { CharacterAssetCache } from './systems/CharacterAssetCache';
import { NPCController } from './systems/NPCController';
import { FollowAI } from './ai/NPCAIBehaviors';
import { CharacterConfigLoader } from './config/CharacterConfigLoader';
import { CombatSystem } from './systems/CombatSystem';
import { DamageReactionSystem } from './systems/DamageReactionSystem';
import { StateManager } from './state/StateManager';
import { CinematicManager } from './systems/CinematicManager';
import { recordingManagerStore } from './systems/animation/RecordingManagerStore';
import './debug-ui';
import './ui/virtual-gamepad';
import './ui/loading-overlay';
import { DebugUiBridge } from './debug-ui/DebugUiBridge';
import { loadingManager } from './core/LoadingManager';
import { computeTangentFrame, worldToTangent } from './systems/camera/TangentSpace';
import type {
  SceneGraphNode,
  PinnedSceneNode,
  SceneGraphMaterialSummary,
  SceneNodeDetails,
  LightProperties,
  LightDebugInfo,
  BehaviorEntityInfo,
  BehaviorParamValue,
} from './debug-ui/types';
import type { Node } from '@babylonjs/core';

// Initialize XState Inspector early (before any actors are created)
// This opens the Stately Inspector in a new tab for live state visualization
// initializeInspector();

import { availableMaps } from './config/maps';
import { Scene, ParticleSystem } from '@babylonjs/core';
import { getActiveMaterialBehaviors, getMaterialBehavior, getMaterialBehaviorByMeshName } from './behaviors/materials/MaterialBehaviorRegistry';

let mapDropdownContainer: HTMLDivElement | null = null;
let mapDropdownElement: HTMLSelectElement | null = null;
let isMapChangeInFlight = false;

// ============================================================================
// Behavior Editor Support Functions
// ============================================================================

/**
 * Collect all entities with behaviors for debug UI editing
 */
function collectBehaviorEntities(scene: Scene): BehaviorEntityInfo[] {
  const entities: BehaviorEntityInfo[] = [];
  
  // Collect particle systems
  for (const ps of scene.particleSystems) {
    if (ps instanceof ParticleSystem) {
      const id = ps.name;
      entities.push({
        id,
        behaviorId: 'emitter:particle',
        displayName: 'Particle Emitter',
        parameters: extractParticleParams(ps),
      });
    }
  }
  
  // Collect material behaviors (water, lava, video) from central registry
  for (const entry of getActiveMaterialBehaviors()) {
    const rawParams = entry.getParams();
    const parameters: Record<string, BehaviorParamValue> = {};
    
    // Convert raw params to BehaviorParamValue with metadata
    for (const [name, value] of Object.entries(rawParams)) {
      parameters[name] = createMaterialParam(entry.behaviorId, name, value);
    }
    
    entities.push({
      id: entry.entityId,
      behaviorId: entry.behaviorId,
      displayName: entry.displayName,
      parameters,
    });
  }
  
  return entities;
}

/**
 * Create a BehaviorParamValue with appropriate metadata for material properties
 */
function createMaterialParam(_behaviorId: string, name: string, value: number | string | boolean): BehaviorParamValue {
  // Common material parameter metadata
  const paramMeta: Record<string, Partial<BehaviorParamValue>> = {
    // Water params
    windForce: { min: -20, max: 20, step: 0.5, group: 'Wind', description: 'Wind force' },
    waveHeight: { min: 0, max: 5, step: 0.05, group: 'Waves', description: 'Wave height' },
    bumpHeight: { min: 0, max: 2, step: 0.01, group: 'Waves', description: 'Bump map intensity' },
    windDirectionX: { min: -2, max: 2, step: 0.1, group: 'Wind', description: 'Wind direction X' },
    windDirectionY: { min: -2, max: 2, step: 0.1, group: 'Wind', description: 'Wind direction Y' },
    waterColorR: { min: 0, max: 1, step: 0.01, group: 'Color', description: 'Water color R' },
    waterColorG: { min: 0, max: 1, step: 0.01, group: 'Color', description: 'Water color G' },
    waterColorB: { min: 0, max: 1, step: 0.01, group: 'Color', description: 'Water color B' },
    colorBlendFactor: { min: 0, max: 1, step: 0.01, group: 'Color', description: 'Color blend factor' },
    waveLength: { min: 0.01, max: 2, step: 0.01, group: 'Waves', description: 'Wave length' },
    
    // Lava params
    flow_speed: { min: 0, max: 1, step: 0.01, group: 'Flow', description: 'Flow speed' },
    flow_direction_x: { min: -2, max: 2, step: 0.1, group: 'Flow', description: 'Flow direction X' },
    flow_direction_y: { min: -2, max: 2, step: 0.1, group: 'Flow', description: 'Flow direction Y' },
    emissive_intensity: { min: 0, max: 10, step: 0.1, group: 'Emission', description: 'Emissive intensity' },
    distortion_scale: { min: 0, max: 0.5, step: 0.005, group: 'Distortion', description: 'Distortion scale' },
    distortion_frequency: { min: 0.1, max: 10, step: 0.1, group: 'Distortion', description: 'Distortion frequency' },
    
    // Video params
    src: { group: 'Source', description: 'Video file path' },
    autoplay: { group: 'Playback', description: 'Auto-play on load' },
    loop: { group: 'Playback', description: 'Loop video' },
  };
  
  const meta = paramMeta[name] || {};
  const type = typeof value === 'boolean' ? 'boolean' : typeof value === 'string' ? 'string' : 'number';
  
  return {
    value,
    type,
    min: meta.min ?? 0,
    max: meta.max ?? 100,
    step: meta.step ?? 0.1,
    group: meta.group ?? 'General',
    description: meta.description ?? name,
  };
}

/**
 * Extract editable parameters from a ParticleSystem
 */
function extractParticleParams(ps: ParticleSystem): Record<string, BehaviorParamValue> {
  const params: Record<string, BehaviorParamValue> = {};
  
  // Emission
  params['emitRate'] = { value: ps.emitRate, type: 'number', min: 0, max: 2000, step: 10, group: 'Emission', description: 'Particles per second' };
  params['minEmitPower'] = { value: ps.minEmitPower, type: 'number', min: 0, max: 50, step: 0.1, group: 'Emission', description: 'Min velocity' };
  params['maxEmitPower'] = { value: ps.maxEmitPower, type: 'number', min: 0, max: 50, step: 0.1, group: 'Emission', description: 'Max velocity' };
  
  // Lifetime
  params['minLifeTime'] = { value: ps.minLifeTime, type: 'number', min: 0.1, max: 60, step: 0.1, group: 'Lifetime', description: 'Minimum life (seconds)' };
  params['maxLifeTime'] = { value: ps.maxLifeTime, type: 'number', min: 0.1, max: 60, step: 0.1, group: 'Lifetime', description: 'Maximum life (seconds)' };
  
  // Size
  params['minSize'] = { value: ps.minSize, type: 'number', min: 0.01, max: 10, step: 0.01, group: 'Size', description: 'Minimum particle size' };
  params['maxSize'] = { value: ps.maxSize, type: 'number', min: 0.01, max: 10, step: 0.01, group: 'Size', description: 'Maximum particle size' };
  
  // Direction
  if (ps.direction1) {
    params['direction1X'] = { value: ps.direction1.x, type: 'number', min: -5, max: 5, step: 0.1, group: 'Direction', description: 'Direction 1 X' };
    params['direction1Y'] = { value: ps.direction1.y, type: 'number', min: -5, max: 5, step: 0.1, group: 'Direction', description: 'Direction 1 Y' };
    params['direction1Z'] = { value: ps.direction1.z, type: 'number', min: -5, max: 5, step: 0.1, group: 'Direction', description: 'Direction 1 Z' };
  }
  if (ps.direction2) {
    params['direction2X'] = { value: ps.direction2.x, type: 'number', min: -5, max: 5, step: 0.1, group: 'Direction', description: 'Direction 2 X' };
    params['direction2Y'] = { value: ps.direction2.y, type: 'number', min: -5, max: 5, step: 0.1, group: 'Direction', description: 'Direction 2 Y' };
    params['direction2Z'] = { value: ps.direction2.z, type: 'number', min: -5, max: 5, step: 0.1, group: 'Direction', description: 'Direction 2 Z' };
  }
  
  // Colors
  if (ps.color1) {
    params['color1R'] = { value: ps.color1.r, type: 'number', min: 0, max: 1, step: 0.01, group: 'Color', description: 'Start color R' };
    params['color1G'] = { value: ps.color1.g, type: 'number', min: 0, max: 1, step: 0.01, group: 'Color', description: 'Start color G' };
    params['color1B'] = { value: ps.color1.b, type: 'number', min: 0, max: 1, step: 0.01, group: 'Color', description: 'Start color B' };
    params['color1A'] = { value: ps.color1.a, type: 'number', min: 0, max: 1, step: 0.01, group: 'Color', description: 'Start color A' };
  }
  if (ps.color2) {
    params['color2R'] = { value: ps.color2.r, type: 'number', min: 0, max: 1, step: 0.01, group: 'Color', description: 'End color R' };
    params['color2G'] = { value: ps.color2.g, type: 'number', min: 0, max: 1, step: 0.01, group: 'Color', description: 'End color G' };
    params['color2B'] = { value: ps.color2.b, type: 'number', min: 0, max: 1, step: 0.01, group: 'Color', description: 'End color B' };
    params['color2A'] = { value: ps.color2.a, type: 'number', min: 0, max: 1, step: 0.01, group: 'Color', description: 'End color A' };
  }
  if (ps.colorDead) {
    params['colorDeadR'] = { value: ps.colorDead.r, type: 'number', min: 0, max: 1, step: 0.01, group: 'Color', description: 'Dead color R' };
    params['colorDeadG'] = { value: ps.colorDead.g, type: 'number', min: 0, max: 1, step: 0.01, group: 'Color', description: 'Dead color G' };
    params['colorDeadB'] = { value: ps.colorDead.b, type: 'number', min: 0, max: 1, step: 0.01, group: 'Color', description: 'Dead color B' };
  }
  
  // Gravity
  if (ps.gravity) {
    params['gravityX'] = { value: ps.gravity.x, type: 'number', min: -50, max: 50, step: 0.5, group: 'Physics', description: 'Gravity X' };
    params['gravityY'] = { value: ps.gravity.y, type: 'number', min: -50, max: 50, step: 0.5, group: 'Physics', description: 'Gravity Y' };
    params['gravityZ'] = { value: ps.gravity.z, type: 'number', min: -50, max: 50, step: 0.5, group: 'Physics', description: 'Gravity Z' };
  }
  
  // Blend mode
  params['blendMode'] = { 
    value: ps.blendMode === ParticleSystem.BLENDMODE_ADD ? 'add' : 'standard', 
    type: 'enum', 
    enumValues: ['standard', 'add', 'multiply'], 
    group: 'Rendering', 
    description: 'Blend mode' 
  };
  
  return params;
}

/**
 * Update a behavior parameter on an entity at runtime
 */
function updateBehaviorParameter(scene: Scene, entityId: string, paramName: string, value: number | string | boolean): void {
  // Find the particle system by name
  for (const ps of scene.particleSystems) {
    if (ps.name === entityId && ps instanceof ParticleSystem) {
      applyParticleParam(ps, paramName, value);
      logger.debug.log(`🎨 Updated ${entityId}.${paramName} = ${value}`);
      return;
    }
  }
  
  logger.debug.warn(`Entity not found: ${entityId}`);
}

/**
 * Apply a parameter change to a ParticleSystem
 */
function applyParticleParam(ps: ParticleSystem, paramName: string, value: number | string | boolean): void {
  const numValue = typeof value === 'number' ? value : parseFloat(value as string);
  
  switch (paramName) {
    // Emission
    case 'emitRate': ps.emitRate = numValue; break;
    case 'minEmitPower': ps.minEmitPower = numValue; break;
    case 'maxEmitPower': ps.maxEmitPower = numValue; break;
    
    // Lifetime
    case 'minLifeTime': ps.minLifeTime = numValue; break;
    case 'maxLifeTime': ps.maxLifeTime = numValue; break;
    
    // Size
    case 'minSize': ps.minSize = numValue; break;
    case 'maxSize': ps.maxSize = numValue; break;
    
    // Direction 1
    case 'direction1X': if (ps.direction1) ps.direction1.x = numValue; break;
    case 'direction1Y': if (ps.direction1) ps.direction1.y = numValue; break;
    case 'direction1Z': if (ps.direction1) ps.direction1.z = numValue; break;
    
    // Direction 2
    case 'direction2X': if (ps.direction2) ps.direction2.x = numValue; break;
    case 'direction2Y': if (ps.direction2) ps.direction2.y = numValue; break;
    case 'direction2Z': if (ps.direction2) ps.direction2.z = numValue; break;
    
    // Color 1
    case 'color1R': if (ps.color1) ps.color1.r = numValue; break;
    case 'color1G': if (ps.color1) ps.color1.g = numValue; break;
    case 'color1B': if (ps.color1) ps.color1.b = numValue; break;
    case 'color1A': if (ps.color1) ps.color1.a = numValue; break;
    
    // Color 2
    case 'color2R': if (ps.color2) ps.color2.r = numValue; break;
    case 'color2G': if (ps.color2) ps.color2.g = numValue; break;
    case 'color2B': if (ps.color2) ps.color2.b = numValue; break;
    case 'color2A': if (ps.color2) ps.color2.a = numValue; break;
    
    // Color Dead
    case 'colorDeadR': if (ps.colorDead) ps.colorDead.r = numValue; break;
    case 'colorDeadG': if (ps.colorDead) ps.colorDead.g = numValue; break;
    case 'colorDeadB': if (ps.colorDead) ps.colorDead.b = numValue; break;
    
    // Gravity
    case 'gravityX': if (ps.gravity) ps.gravity.x = numValue; break;
    case 'gravityY': if (ps.gravity) ps.gravity.y = numValue; break;
    case 'gravityZ': if (ps.gravity) ps.gravity.z = numValue; break;
    
    // Blend mode
    case 'blendMode':
      if (value === 'add') ps.blendMode = ParticleSystem.BLENDMODE_ADD;
      else if (value === 'multiply') ps.blendMode = ParticleSystem.BLENDMODE_MULTIPLY;
      else ps.blendMode = ParticleSystem.BLENDMODE_STANDARD;
      break;
  }
}

// Enable matrix interpolation for smooth skeleton animation blending
Animation.AllowMatricesInterpolation = true;

// Get canvas element
const canvas = document.getElementById('renderCanvas') as HTMLCanvasElement;
if (!canvas) {
  throw new Error('Canvas element not found');
}

// Initialize core systems
const sceneManager = new SceneManager(canvas);
const scene = sceneManager.getScene();
const engine = sceneManager.getEngine();
const entityRegistry = new EntityRegistry();
EntityRegistry.setInstance(entityRegistry); // Make available via singleton accessor

// Shader recompilation detection - helps identify what's causing visual glitches
// (currently disabled - enable by uncommenting the createEffect override below)

// // Track shader compilations via Effect creation
// let shaderCompilationCount = 0;
// const shaderCompilationStartTime = performance.now();
// const recompilationHistory = new Map<string, number>(); // Track unique shader variants
// let currentMaterialBeingChecked: string | null = null; // Track which material is being processed
// const originalCreateEffect = engine.createEffect.bind(engine);
// engine.createEffect = function(...args: Parameters<typeof originalCreateEffect>) {
//   shaderCompilationCount++;
//   const elapsed = (performance.now() - shaderCompilationStartTime).toFixed(0);
//   const frameId = scene.getFrameId();
//   const isAfterInit = Number(elapsed) > 5000; // After 5 seconds, consider it "runtime" compilation
//   const effectName = typeof args[0] === 'string' ? args[0] : (args[0] as any)?.vertex ?? 'unknown';
  
//   if (isAfterInit) {
//     // Runtime shader compilation - this causes visual glitches!
//     const options = args[1] as any;
//     const defines = options?.defines ?? options ?? '';
//     const definesStr = typeof defines === 'string' ? defines : JSON.stringify(defines);
    
//     // Create a hash of the shader variant (use full defines for uniqueness)
//     const variantKey = `${effectName}:${definesStr}`;
//     const prevCount = recompilationHistory.get(variantKey) ?? 0;
//     recompilationHistory.set(variantKey, prevCount + 1);
    
//     // Only log first occurrence of each unique variant, or every 500th repeat
//     if (prevCount === 0 || prevCount % 500 === 0) {
//       const repeatInfo = prevCount > 0 ? ` (repeated ${prevCount}x!)` : '';
//       const materialInfo = currentMaterialBeingChecked ? ` [Material: ${currentMaterialBeingChecked}]` : '';
//       // Show FULL defines for first occurrence to debug
//       const definesDisplay = prevCount === 0 ? definesStr : definesStr.slice(0, 300);
//       console.warn(`🔥 SHADER RECOMPILATION #${shaderCompilationCount} frame:${frameId}${repeatInfo}: ${effectName}${materialInfo}`);
//       console.warn(`Defines (${definesStr.length} chars): ${definesDisplay}`);
      
//       // For video materials, just note it and skip stack
//       if (effectName.includes('video') || (currentMaterialBeingChecked && currentMaterialBeingChecked.includes('video'))) {
//         console.warn('  (video material - dynamic by design)');
//       } else if (prevCount === 0) {
//         // First time - show stack
//         const stack = new Error().stack?.split('\n').slice(2, 6).join('\n') ?? 'no stack';
//         console.warn('Stack:', stack);
//       }
//     }
//   } else {
//     // Initial compilation during load - this is expected
//     logger.performance.debug(`Shader compilation #${shaderCompilationCount}: ${effectName}`);
//   }
  
//   return originalCreateEffect(...args);
// };

// // Patch PBRMaterial to track which material is being checked
// import { PBRMaterial } from '@babylonjs/core';
// const originalIsReadyForSubMesh = PBRMaterial.prototype.isReadyForSubMesh;
// const problematicMaterials = new Set<string>();
// PBRMaterial.prototype.isReadyForSubMesh = function(mesh: any, subMesh: any, useInstances: any) {
//   currentMaterialBeingChecked = this.name;
//   const result = originalIsReadyForSubMesh.call(this, mesh, subMesh, useInstances);
  
//   // Skip video materials - they're dynamic by design and expected to update every frame
//   const isVideoMaterial = this.name.toLowerCase().includes('video');
  
//   // Track materials that return false (not ready) after init
//   const elapsed = performance.now() - shaderCompilationStartTime;
//   if (!result && elapsed > 5000 && !problematicMaterials.has(this.name) && !isVideoMaterial) {
//     problematicMaterials.add(this.name);
//     const scene = this.getScene();
//     console.warn(`⚠️ Material "${this.name}" is NOT READY. Investigating...`);
//     console.warn(`  - checkReadyOnEveryCall: ${this.checkReadyOnEveryCall}`);
//     console.warn(`  - checkReadyOnlyOnce: ${this.checkReadyOnlyOnce}`);
//     console.warn(`  - isFrozen: ${this.isFrozen}`);
//     console.warn(`  - albedoTexture ready: ${this.albedoTexture?.isReady?.() ?? 'no texture'}`);
//     console.warn(`  - bumpTexture ready: ${this.bumpTexture?.isReady?.() ?? 'no texture'}`);
//     console.warn(`  - metallicTexture ready: ${this.metallicTexture?.isReady?.() ?? 'no texture'}`);
//     console.warn(`  - reflectionTexture ready: ${this.reflectionTexture?.isReady?.() ?? 'no texture'}`);
//     console.warn(`  - environmentBRDFTexture ready: ${this.environmentBRDFTexture?.isReady?.() ?? 'no texture'}`);
//     console.warn(`  - mesh: ${mesh?.name}`);
//     // Check for material plugins
//     const plugins = (this as any)._materialPluginManager?.getActivePlugins?.();
//     console.warn(`  - material plugins: ${plugins?.map((p: any) => p.constructor.name).join(', ') ?? 'none'}`);
//     console.warn(`  --- Scene-level ---`);
//     console.warn(`  - scene.environmentTexture ready: ${scene?.environmentTexture?.isReady?.() ?? 'no env texture'}`);
//     console.warn(`  - scene.isReady: ${scene?.isReady?.()}`);
//     console.warn(`  - scene.getWaitingItemsCount: ${scene?.getWaitingItemsCount?.()}`);
//     console.warn(`  - enabled lights: ${scene?.lights?.filter((l: any) => l.isEnabled()).length} / ${scene?.lights?.length}`);
//     console.warn(`  - subMesh._materialDefines: ${subMesh?._materialDefines ? 'exists' : 'null'}`);
//     console.warn(`  - subMesh effect ready: ${subMesh?.effect?.isReady?.() ?? 'no effect'}`);
//   }
  
//   currentMaterialBeingChecked = null;
//   return result;
// };

// // Also patch StandardMaterial
// const originalStdIsReadyForSubMesh = StandardMaterial.prototype.isReadyForSubMesh;
// StandardMaterial.prototype.isReadyForSubMesh = function(mesh, subMesh, useInstances) {
//   currentMaterialBeingChecked = this.name;
//   const result = originalStdIsReadyForSubMesh.call(this, mesh, subMesh, useInstances);
//   currentMaterialBeingChecked = null;
//   return result;
// };

// Register built-in behaviors before any entities are spawned
registerBuiltInBehaviors();

const eventBus = new EventBus();

// Connect gameTime to EventBus for broadcasting time changes
gameTime.setEventBus(eventBus);

// Connect loadingManager to EventBus for loading events
loadingManager.setEventBus(eventBus);

// Check URL parameter for map override
const urlParams = new URLSearchParams(window.location.search);
const urlMapParam = urlParams.get('map');

const savedMap = localStorage.getItem('himum-selected-map');
const forcedMap = localStorage.getItem('himum-test-map');
const defaultMap = 'assets/maps/tooling-test.glb';
const levelConfig = {
  mapPath: urlMapParam || forcedMap || savedMap || defaultMap,
  mapScale: 1.0,
};

// Keep the selector in sync even when a test map is forced via localStorage.
if (levelConfig.mapPath && levelConfig.mapPath !== savedMap) {
  localStorage.setItem('himum-selected-map', levelConfig.mapPath);
}

// Initialize debug renderer
DebugRenderer.getInstance(scene);

// Initialize physics
const physicsManager = new PhysicsManager(scene);

// Register scene with logger for observability API
logger.setScene(scene);

// Expose debug globals for browser console and Playwright script access
(window as any).physicsManager = physicsManager;
(window as any).gameScene = scene;
(window as any).scene = scene; // Also as window.scene for convenience

// Initialize input system
const input = new InputManager(scene);

// Mobile (phone/tablet): virtual gamepad overlay
const shouldEnableVirtualGamepad = (): boolean => {
  // Phone/tablet heuristic: coarse pointer, no hover, and constrained width.
  // Avoid enabling on touch laptops by default.
  try {
    const coarse = window.matchMedia?.('(pointer: coarse)')?.matches ?? false;
    const noHover = window.matchMedia?.('(hover: none)')?.matches ?? false;
    const small = window.matchMedia?.('(max-width: 1024px)')?.matches ?? false;

    // Dev-only override for quick testing.
    const params = new URLSearchParams(window.location.search);
    const force = params.get('vgp') === '1';
    return force || (coarse && noHover && small);
  } catch {
    return false;
  }
};

// Store virtual gamepad reference for stats updates
let virtualGamepad: import('./ui/virtual-gamepad/himum-virtual-gamepad').HimumVirtualGamepad | null = null;

if (shouldEnableVirtualGamepad()) {
  const vgp = document.createElement('himum-virtual-gamepad') as import('./ui/virtual-gamepad/himum-virtual-gamepad').HimumVirtualGamepad;
  vgp.setAttribute('visible', 'true');
  vgp.setAttribute('aria-hidden', 'true');
  document.body.appendChild(vgp);
  input.setVirtualGamepadEnabled(true);
  virtualGamepad = vgp;

  vgp.addEventListener('himum-virtual-gamepad-change', (e: Event) => {
    const ce = e as CustomEvent;
    const detail = (ce.detail || {}) as any;
    input.setVirtualGamepadState(detail);
  });

  // Handle resolution scaling from mobile debug menu
  vgp.addEventListener('himum-resolution-change', (e: Event) => {
    const ce = e as CustomEvent<number>;
    const scale = ce.detail;
    if (typeof scale === 'number' && scale >= 1) {
      engine.setHardwareScalingLevel(scale);
      logger.debug.log(`📱 Resolution scale set to 1/${scale}`);
    }
  });
}

// Track current leader character
let currentLeaderId = 'lilly';
let partyLeaderIndex = 0;

// Follow distance tuning (meters along the spline)
const FOLLOW_MIN_DISTANCE_METERS = 2.0;
const FOLLOW_MAX_DISTANCE_METERS = 4.0;

// Party configuration (loaded from JSON configs in initGame)
let partyMembers: { id: string; config: any }[] = [];

const partyNPCs: NPCController[] = [];
const partyCharacters: Array<NPCController | null> = [];

let playerController: NPCController;
let partyManager: PartyManager;
let npcSpawnManager: NPCSpawnManager;

const updateMapDropdownSelection = (mapPath: string): void => {
  if (!mapDropdownElement) return;

  const options = Array.from(mapDropdownElement.options);
  const hasOption = options.some((opt) => opt.value === mapPath);
  if (!hasOption) {
    const option = document.createElement('option');
    option.value = mapPath;
    option.textContent = mapPath.split('/').pop() ?? mapPath;
    mapDropdownElement.appendChild(option);
  }

  mapDropdownElement.value = mapPath;
};

const ensureMapDropdown = (): void => {
  // Only show on mobile devices
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768;
  if (!isMobile) return;

  if (mapDropdownContainer || availableMaps.length === 0) return;

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.top = '16px';
  container.style.left = '16px';
  container.style.zIndex = '6000';
  container.style.display = 'flex';
  container.style.alignItems = 'center';
  container.style.gap = '8px';
  container.style.padding = '8px 12px';
  container.style.borderRadius = '10px';
  container.style.background = 'rgba(0, 0, 0, 0.55)';
  container.style.backdropFilter = 'blur(8px)';
  container.style.pointerEvents = 'auto';

  const label = document.createElement('span');
  label.textContent = 'Level';
  label.style.fontFamily = 'ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif';
  label.style.fontSize = '14px';
  label.style.color = 'rgba(255, 255, 255, 0.9)';

  const select = document.createElement('select');
  select.style.fontFamily = 'ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif';
  select.style.fontSize = '14px';
  select.style.padding = '4px 10px';
  select.style.borderRadius = '8px';
  select.style.border = '1px solid rgba(255, 255, 255, 0.25)';
  select.style.background = 'rgba(15, 15, 15, 0.85)';
  select.style.color = 'rgba(255, 255, 255, 0.95)';
  select.style.outline = 'none';
  select.style.pointerEvents = 'auto';

  for (const map of availableMaps) {
    const option = document.createElement('option');
    option.value = map.path;
    option.textContent = map.filename;
    select.appendChild(option);
  }

  select.addEventListener('change', () => {
    const next = select.value;
    if (!next) return;
    if (next === levelConfig.mapPath) {
      updateMapDropdownSelection(next);
      return;
    }

    localStorage.setItem('himum-selected-map', next);
    void changeMap(next);
  });

  mapDropdownElement = select;
  mapDropdownContainer = container;

  container.appendChild(label);
  container.appendChild(select);
  document.body.appendChild(container);

  updateMapDropdownSelection(levelConfig.mapPath);
};

async function changeMap(mapPath: string): Promise<void> {
  if (isMapChangeInFlight) {
    logger.level.warn('Map change already in progress; ignoring duplicate request.');
    return;
  }

  isMapChangeInFlight = true;
  try {
    logger.level.log(`🗺️ Changing map to: ${mapPath}`);

    levelConfig.mapPath = mapPath;
    localStorage.setItem('himum-selected-map', mapPath);
    updateMapDropdownSelection(mapPath);

    // === CLEANUP PHASE ===
    // Stop any active cinematics first
    CinematicManager.stop();
    
    // Dispose player and party NPCs
    if (playerController) {
      playerController.dispose();
    }

    for (const npc of partyCharacters) {
      if (npc) npc.dispose();
    }
    partyCharacters.length = 0;

    for (const npc of partyNPCs) {
      if (npc) npc.dispose();
    }
    partyNPCs.length = 0;

    // Clear NPC spawn manager (despawns world NPCs)
    npcSpawnManager.clear();
    
    // Clear entity registry (removes all entity info)
    entityRegistry.clear();
    
    // Clear particle systems
    particleManager.clear();
    
    // Unload level resources (meshes, materials, lights, etc.)
    levelBuilder.unload();
    
    // Clear physics bodies
    const bodyIds = Array.from(physicsManager['bodies'].keys());
    for (const id of bodyIds) {
      physicsManager.removeBody(id);
    }
    logger.physics.log('Physics bodies cleared for map reload');
    
    // Clear cinematic animations (registered from previous map)
    CinematicManager.clear();

    // === REINITIALIZE PHASE ===
    await initGame();

    logger.level.log('✅ Map reloaded successfully');
  } finally {
    isMapChangeInFlight = false;
  }
}

// Note: Time scaling is now managed by the GameTime system (src/core/GameTime.ts)
// Access via: gameTime.timeScale, gameTime.pause(), gameTime.setTimeScale(), etc.

const testHooks = {
  isReady: (): boolean => !!playerController && !!scene && !!physicsManager,
  getPlayerState: () => {
    if (!playerController) return null;
    const pos = playerController.character.position;
    const vel = playerController.velocity;
    return {
      grounded: playerController.isGrounded,
      position: { x: pos.x, y: pos.y, z: pos.z },
      velocity: { x: vel.x, y: vel.y, z: vel.z },
      playerT: playerController.playerT,
    };
  },
  forceMap: async (mapPath: string) => {
    await changeMap(mapPath);
  },
  // Expose scene for debugging
  get scene() { return scene; },
  // Time scale control - delegates to gameTime system
  get timeScale() { return gameTime.rawTimeScale; },
  set timeScale(value: number) { gameTime.setTimeScale(Math.max(0, Math.min(10, value))); },
  setTimeScale(value: number) { gameTime.setTimeScale(Math.max(0, Math.min(10, value))); return gameTime.rawTimeScale; },
  // Expose full gameTime API for advanced control
  gameTime,
};
(window as any).__himumTestHooks = testHooks;

// Debug rendering toggle ('.' key) and HUD integration
let debugRenderingEnabled = false; // OFF by default
let combatDebugEnabled = false; // Combat hitbox/collision debug
let collisionDebugMeshes: Mesh[] = []; // Wireframe meshes for collision geometry
let lightDebugMeshes: Mesh[] = []; // Debug visualization meshes for lights
let lightDebugEnabled = false; // Track if light debug is active for render loop updates
// Map from debug mesh name to source light name for dynamic updates
const lightDebugMeshMap = new Map<string, string>();

// Debug HUD state backing variables (mutated during init)
let activeLoadedSpline: any | null = null;
let splineDebugMesh: Mesh | null = null;
let highlightMesh: Mesh | null = null;

// Pixelation animation state (was previously in GameUI)
let animatePixelation = false;
let pixelationTime = 0;
let pixelationEnabled = false;

// Scene picking mode (click in scene to select in inspector)
let scenePickModeEnabled = false;
let scenePickObserver: any | null = null;

// Hidden material preview pipeline (single-instance, reused across refreshes)
let hudPreviewSphere: Mesh | null = null;
let hudPreviewCamera: FreeCamera | null = null;
let hudPreviewLight: HemisphericLight | null = null;
let hudPreviewRtt: RenderTargetTexture | null = null;
const hudMaterialPreviewCache = new Map<string, string>();

// Initialize character asset cache for fast switching
const characterCache = new CharacterAssetCache(scene);

// Initialize gameplay systems
const splineLoader = new SplineLoader(scene);
const triggerSystem = new TriggerSystem(entityRegistry, eventBus);
triggerSystem.setScene(scene);  // Enable debug visualization support

// Create gameplay spline for 2.5D constraint
const gameplaySpline = splineLoader.createTestGameplaySpline();
// splineLoader.visualizeSpline(gameplaySpline, 'gameplaySpline', new Color3(1, 0, 1));

// Create spline constraint system
const splineConstraint = new SplineConstraint(gameplaySpline);

// Create camera
const camera = new ArcRotateCamera(
  'camera',
  Math.PI / 2,     // alpha (horizontal rotation)
  Math.PI / 3,     // beta (vertical rotation)
  10,              // radius
  new Vector3(0, 1, 0),  // target
  scene
);
camera.lowerRadiusLimit = 5;
camera.upperRadiusLimit = 20;
// Exclude debug layer (bit 29) by default
const DEBUG_LAYER_MASK = 0x20000000;
// Reserved for engine-driven HUD previews (bit 30)
const PREVIEW_LAYER_MASK = 0x40000000;
camera.layerMask = 0xFFFFFFFF & ~DEBUG_LAYER_MASK;
camera.layerMask = camera.layerMask & ~PREVIEW_LAYER_MASK;

scene.activeCameras = [camera];

// Free-roam camera (toggleable)
let freeRoamCamera: FreeCamera | null = null;
let freeRoamEnabled = false;

const applyMainCameraLayerMask = (): void => {
  const mask = debugRenderingEnabled
    ? (0xFFFFFFFF & ~PREVIEW_LAYER_MASK)
    : (0xFFFFFFFF & ~DEBUG_LAYER_MASK & ~PREVIEW_LAYER_MASK);
  camera.layerMask = mask;
  if (freeRoamCamera) freeRoamCamera.layerMask = mask;
};

const setFreeRoamEnabled = (enabled: boolean): void => {
  if (freeRoamEnabled === enabled) return;
  freeRoamEnabled = enabled;

  if (enabled) {
    // Free-roam uses WASD/mouse; disable character movement input.
    input.setMovementEnabled(false);

    if (!freeRoamCamera) {
      freeRoamCamera = new FreeCamera('freeRoamCamera', camera.position.clone(), scene);
      freeRoamCamera.setTarget(camera.getTarget());
      freeRoamCamera.speed = 0.25;
      freeRoamCamera.angularSensibility = 2500;
      // WASD
      freeRoamCamera.keysUp = [87];
      freeRoamCamera.keysDown = [83];
      freeRoamCamera.keysLeft = [65];
      freeRoamCamera.keysRight = [68];
      // Keep arrow keys as well.
      freeRoamCamera.keysUp.push(38);
      freeRoamCamera.keysDown.push(40);
      freeRoamCamera.keysLeft.push(37);
      freeRoamCamera.keysRight.push(39);
    } else {
      freeRoamCamera.position.copyFrom(camera.position);
      freeRoamCamera.setTarget(camera.getTarget());
    }

    applyMainCameraLayerMask();

    try {
      camera.detachControl();
    } catch {
      // ignore
    }
    freeRoamCamera.attachControl(canvas, true);
    scene.activeCamera = freeRoamCamera;
    scene.activeCameras = [freeRoamCamera];
    logger.camera.log('Free-roam camera: ON (F6 to toggle)');
    return;
  }

  // Back to follow camera
  input.setMovementEnabled(true);
  try {
    freeRoamCamera?.detachControl();
  } catch {
    // ignore
  }
  camera.attachControl(canvas, true);
  applyMainCameraLayerMask();
  scene.activeCamera = camera;
  scene.activeCameras = [camera];
  logger.camera.log('Free-roam camera: OFF (F6 to toggle)');
};

// Initialize camera controller
const cameraController = new CameraController(camera);
cameraController.addSegment({
  id: 'main',
  startT: 0.0,
  endT: 1.0,
  type: 'follow-side',
  data: {
    offset: new Vector3(10, 3, 100),
    distance: -8,
    height: 4,
    smoothing: 0.1,
    spline: gameplaySpline
  },
  transitionDuration: 1.0,
  transitionEasing: 'ease-in-out'
});

// Initialize post-processing manager
const postProcessManager = new PostProcessManager(scene, camera);
pixelationEnabled = postProcessManager.isEnabled();

// Initialize impact effects manager (screen shake, ripple effects)
const impactEffectManager = new ImpactEffectManager(scene, camera, eventBus);

// Initialize particle manager
const particleManager = new ParticleManager(scene, eventBus);

// Initialize combat system
const combatSystem = new CombatSystem(scene, eventBus);
const damageReactionSystem = new DamageReactionSystem(eventBus);

// Track active camera trigger for debug UI (used in bridge callbacks)
let activeCameraTrigger: string | null = null;

// Helper function to build environment info for debug UI
const buildEnvironmentInfo = () => {
  const mapAmbient = environmentSettings.getMapAmbientColor();
  const defaultCamera = environmentSettings.getDefaultCameraConfig();
  const activeCamera = freeRoamCamera && freeRoamEnabled ? freeRoamCamera : camera;
  const cameraTarget = activeCamera.getTarget();
  const skyboxInfo = levelBuilder.getEnvironmentInfo();
  const fogEnabled = environmentSettings.getFogEnabled();
  const fogDensity = environmentSettings.getFogDensity();
  const fogColor = environmentSettings.getFogColor();
  
  return {
    ambientColor: mapAmbient ? { r: mapAmbient.r, g: mapAmbient.g, b: mapAmbient.b } : null,
    ambientColorHex: mapAmbient ? `#${Math.round(mapAmbient.r * 255).toString(16).padStart(2, '0')}${Math.round(mapAmbient.g * 255).toString(16).padStart(2, '0')}${Math.round(mapAmbient.b * 255).toString(16).padStart(2, '0')}` : null,
    defaultCamera: defaultCamera ? {
      type: defaultCamera.type,
      offset: defaultCamera.type === 'follow' && defaultCamera.follow ? 
        { x: defaultCamera.follow.offset.x, y: defaultCamera.follow.offset.y, z: defaultCamera.follow.offset.z } : null,
      smoothing: defaultCamera.type === 'follow' && defaultCamera.follow ? defaultCamera.follow.smoothing : 0,
      fov: defaultCamera.type === 'follow' && defaultCamera.follow ? defaultCamera.follow.fov || 60 : 60
    } : null,
    activeCameraTrigger,
    activeCamera: {
      name: activeCamera.name,
      position: {
        x: activeCamera.position.x,
        y: activeCamera.position.y,
        z: activeCamera.position.z
      },
      target: {
        x: cameraTarget.x,
        y: cameraTarget.y,
        z: cameraTarget.z
      }
    },
    skybox: skyboxInfo,
    fog: {
      enabled: fogEnabled,
      density: fogDensity,
      color: { r: fogColor.r, g: fogColor.g, b: fogColor.b }
    }
  };
};

// Helper function to build light debug info for the lighting panel
const buildLightDebugInfo = (): LightDebugInfo[] => {
  // Get player position for distance calculation
  const playerPos = playerController?.character?.position;
  
  return scene.lights.map((light: Light): LightDebugInfo => {
    // Determine light type
    let type: LightDebugInfo['type'] = 'Point';
    if (light instanceof HemisphericLight) type = 'Hemispheric';
    else if (light instanceof SpotLight) type = 'Spot';
    else if (light instanceof DirectionalLight) type = 'Directional';
    else if (light instanceof PointLight) type = 'Point';
    
    // For pool lights, determine source and enabled state from UnifiedLightManager
    let source: LightDebugInfo['source'] = 'unknown';
    let effectivelyEnabled = light.isEnabled();
    
    if (light.name.startsWith('pool_light_')) {
      // Pool lights: check what request they're assigned to
      const assignedRequest = UnifiedLightManager.getAssignedRequestForLight(light.name);
      if (assignedRequest) {
        source = assignedRequest.isCombat ? 'combat' : 'scene';
        effectivelyEnabled = true;
      } else {
        // Unused pool light - mark as inactive
        source = 'scene';
        effectivelyEnabled = false;
      }
    } else if (light.name.startsWith('channel_light_')) {
      source = 'combat';
    } else if (light.name.startsWith('character_')) {
      source = 'character';
    } else {
      source = 'scene';
    }
    
    // Get position (not all lights have position)
    let position: { x: number; y: number; z: number } | undefined;
    let distanceToPlayer: number | undefined;
    if ('position' in light && light.position) {
      const pos = light.position as Vector3;
      position = { x: pos.x, y: pos.y, z: pos.z };
      
      // Show raw distance in UI (more intuitive for understanding light positions)
      // Note: Culling uses effective distance (raw - range) for decisions
      if (playerPos) {
        distanceToPlayer = Vector3.Distance(pos, playerPos);
      }
    }
    
    // Get range (only PointLight and SpotLight have range)
    let range: number | undefined;
    if ('range' in light) {
      range = (light as PointLight | SpotLight).range;
    }
    
    return {
      name: light.name,
      type,
      enabled: effectivelyEnabled,
      intensity: light.intensity,
      range,
      position,
      distanceToPlayer,
      color: { 
        r: light.diffuse.r, 
        g: light.diffuse.g, 
        b: light.diffuse.b 
      },
      source,
    };
  });
};

// Scene Graph Dirty Tracking
let sceneGraphDirty = false;
let lastSceneGraphRefresh = 0;
const SCENE_GRAPH_REFRESH_RATE = 1000; // Max once per second
let isExplorerPanelVisible = false;
let lastPinnedNodeIds: string[] = [];
let lastSelectedNodeId: string | null | undefined = null;

const markSceneGraphDirty = () => {
  sceneGraphDirty = true;
};

scene.onNewMeshAddedObservable.add(markSceneGraphDirty);
scene.onMeshRemovedObservable.add(markSceneGraphDirty);
scene.onNewTransformNodeAddedObservable.add(markSceneGraphDirty);
scene.onTransformNodeRemovedObservable.add(markSceneGraphDirty);

const doRefreshSceneGraph = async (pinnedNodeIds: string[], selectedNodeId?: string | null) => {
    const nodeById = new Map<string, Node>();

    const ensureMaterialPreviewPipeline = (): RenderTargetTexture | null => {
      if (hudPreviewRtt && hudPreviewSphere && hudPreviewCamera) return hudPreviewRtt;

      hudPreviewSphere = MeshBuilder.CreateSphere(
        'hud_material_preview_sphere',
        { diameter: 2, segments: 32 },
        scene
      );
      hudPreviewSphere.isPickable = false;
      hudPreviewSphere.layerMask = PREVIEW_LAYER_MASK;

      hudPreviewCamera = new FreeCamera(
        'hud_material_preview_camera',
        new Vector3(0, 0, -3.2),
        scene
      );
      hudPreviewCamera.layerMask = PREVIEW_LAYER_MASK;
      hudPreviewCamera.setTarget(Vector3.Zero());

      hudPreviewLight = new HemisphericLight(
        'hud_material_preview_light',
        new Vector3(0.4, 1.0, -0.2),
        scene
      );
      // Keep this light from affecting the main scene.
      (hudPreviewLight as any).includeOnlyWithLayerMask = PREVIEW_LAYER_MASK;
      hudPreviewLight.intensity = 1.25;

      hudPreviewRtt = new RenderTargetTexture(
        'hud_material_preview_rtt',
        { width: 256, height: 256 },
        scene
      );
      (hudPreviewRtt as any).activeCamera = hudPreviewCamera;
      hudPreviewRtt.renderList = [hudPreviewSphere];
      hudPreviewRtt.clearColor = new Color4(0, 0, 0, 0);
      // Render only when explicitly requested.
      (hudPreviewRtt as any).refreshRate = (RenderTargetTexture as any).REFRESHRATE_RENDER_ONCE ?? 0;

      scene.customRenderTargets.push(hudPreviewRtt);
      return hudPreviewRtt;
    };

    const tryBuildTexturePreviewDataUrl = async (texture: any, maxSize = 160): Promise<string | undefined> => {
      try {
        if (!texture) return undefined;
        if (typeof texture.isReady === 'function' && !texture.isReady()) return undefined;
        if (typeof texture.readPixels !== 'function') return undefined;
        if (typeof texture.getSize !== 'function') return undefined;

        const size = texture.getSize();
        const fullW = typeof size === 'number' ? size : size?.width;
        const fullH = typeof size === 'number' ? size : size?.height;
        if (!fullW || !fullH) return undefined;

        const pixelsAny = await texture.readPixels();
        if (!pixelsAny) return undefined;

        const pixels = pixelsAny instanceof Uint8Array ? pixelsAny : new Uint8Array((pixelsAny as ArrayBufferView).buffer);
        const expected = fullW * fullH * 4;
        if (pixels.length < expected) return undefined;

        const srcCanvas = document.createElement('canvas');
        srcCanvas.width = fullW;
        srcCanvas.height = fullH;
        const srcCtx = srcCanvas.getContext('2d');
        if (!srcCtx) return undefined;

        // Babylon readPixels is bottom-left origin; flip vertically for browser.
        const data = new Uint8ClampedArray(expected);
        for (let y = 0; y < fullH; y++) {
          const srcRow = (fullH - 1 - y) * fullW * 4;
          const dstRow = y * fullW * 4;
          data.set(pixels.subarray(srcRow, srcRow + fullW * 4), dstRow);
        }
        srcCtx.putImageData(new ImageData(data, fullW, fullH), 0, 0);

        const scale = Math.min(1, maxSize / Math.max(fullW, fullH));
        const outW = Math.max(1, Math.round(fullW * scale));
        const outH = Math.max(1, Math.round(fullH * scale));

        const outCanvas = document.createElement('canvas');
        outCanvas.width = outW;
        outCanvas.height = outH;
        const outCtx = outCanvas.getContext('2d');
        if (!outCtx) return undefined;

        outCtx.imageSmoothingEnabled = true;
        outCtx.drawImage(srcCanvas, 0, 0, outW, outH);
        return outCanvas.toDataURL('image/png');
      } catch {
        return undefined;
      }
    };

    const buildMaterialPreviewDataUrl = async (materialAny: any): Promise<string | undefined> => {
      try {
        if (!materialAny) return undefined;

        // Skip special materials that have internal render targets or shaders
        // that get corrupted when assigned to different meshes
        const className = materialAny.getClassName?.();
        const materialName = materialAny.name ?? '';
        if (
          className === 'WaterMaterial' ||
          materialName.startsWith('lavaMaterial_') ||
          materialName.startsWith('videoMaterial_')
        ) {
          return undefined;
        }

        const cacheKey = String(materialAny.uniqueId ?? materialAny.id ?? materialAny.name ?? 'mat');
        const cached = hudMaterialPreviewCache.get(cacheKey);
        if (cached) return cached;

        const rtt = ensureMaterialPreviewPipeline();
        if (!rtt || !hudPreviewSphere) return undefined;

        hudPreviewSphere.material = materialAny;

        // Force a render of the RTT and then read it back.
        (rtt as any).render?.(true);

        // Reuse the existing readPixels->PNG helper.
        const url = await tryBuildTexturePreviewDataUrl(rtt, 200);
        if (url) hudMaterialPreviewCache.set(cacheKey, url);
        return url;
      } catch {
        return undefined;
      }
    };

    const textureSlots = [
      'albedoTexture',
      'diffuseTexture',
      'baseTexture',
      'emissiveTexture',
      'normalTexture',
      'bumpTexture',
      'metallicTexture',
      'roughnessTexture',
      'ambientTexture',
      'opacityTexture',
    ];

    const isValidPreviewUrl = (url: string | undefined): url is string => {
      if (!url) return false;
      const trimmed = url.trim();
      if (!trimmed) return false;
      // Babylon GLB embedded image references sometimes come through like: "data:assets/...#image1".
      // Those aren't real data URLs and will fail in <img>.
      if (trimmed.startsWith('data:assets/') || trimmed.startsWith('data:/assets/')) return false;
      if (trimmed.startsWith('data:') && !trimmed.startsWith('data:image/')) return false;
      return true;
    };

    const nodeTypeName = (n: any): string => (n as any).getClassName?.() ?? (n as any).constructor?.name ?? 'Node';
    const nodePathSegment = (n: any): string => `${nodeTypeName(n)}:${n?.name ?? '(unnamed)'}`;
    const computeNodePath = (n: any): string => {
      const segments: string[] = [];
      let cur: any = n;
      while (cur) {
        segments.push(nodePathSegment(cur));
        cur = (cur as any).parent;
      }
      segments.reverse();
      return segments.join('/');
    };

    const pickFirstPreviewTexture = (mat: any): any | undefined => {
      if (!mat) return undefined;
      const candidates = [
        mat.albedoTexture,
        mat.baseTexture,
        mat.diffuseTexture,
        mat.baseColorTexture,
        mat.emissiveTexture,
        mat.ambientTexture,
        mat.opacityTexture,
        mat.reflectionTexture,
        mat.metallicTexture,
        mat.metallicRoughnessTexture,
        mat.normalTexture,
        mat.bumpTexture,
      ].filter(Boolean);
      return candidates[0];
    };

    const buildMaterialSummary = (mat: any): SceneGraphMaterialSummary | null => {
      if (!mat) return null;
      const className = typeof mat.getClassName === 'function' ? mat.getClassName() : (mat.constructor?.name ?? 'Material');
      const effect = typeof mat.getEffect === 'function' ? mat.getEffect() : null;
      const effectName = effect?.name ?? effect?._key ?? undefined;
      const isFrozen = typeof mat.isFrozen === 'function' ? mat.isFrozen() : undefined;
      const backFaceCulling = typeof mat.backFaceCulling === 'boolean' ? mat.backFaceCulling : undefined;

      const textures: Array<{ slot: string; name?: string; url?: string }> = [];
      for (const slot of textureSlots) {
        const t = mat[slot];
        if (!t) continue;
        const url = typeof t.url === 'string' ? t.url : undefined;
        const name = typeof t.name === 'string' ? t.name : undefined;
        textures.push({ slot, name, url });
      }

      const previewTextureUrl = textures.find((t) => isValidPreviewUrl(t.url))?.url;
      return {
        name: typeof mat.name === 'string' ? mat.name : undefined,
        className,
        effectName,
        isFrozen,
        backFaceCulling,
        previewTextureUrl,
        textures: textures.length ? textures : undefined,
      };
    };

    const visited = new Set<Node>();

    // Map mesh IDs to Entity IDs
    const meshToEntity = new Map<string, string>();
    partyCharacters.forEach(c => {
      if (c?.character) {
        meshToEntity.set(String(c.character.uniqueId), c.getId());
        meshToEntity.set(c.character.id, c.getId());
      }
    });
    
    // Add dynamic objects to entity map
    if (dynamicObjectSystem) {
      const objects = dynamicObjectSystem.getAllObjects();
      for (const obj of objects) {
        // Map the root node of the asset
        if (obj.asset && obj.asset.root) {
          meshToEntity.set(String(obj.asset.root.uniqueId), obj.id);
          meshToEntity.set(obj.asset.root.id, obj.id);
          meshToEntity.set(obj.asset.root.name, obj.id);
          
          // Also map all child meshes
          const children = obj.asset.root.getChildMeshes();
          for (const child of children) {
            meshToEntity.set(String(child.uniqueId), obj.id);
            meshToEntity.set(child.id, obj.id);
          }
        }
      }
    }

    const toGraphNode = (n: Node, parentPath?: string): SceneGraphNode | null => {
      // Prevent duplicate/infinite traversal
      if (visited.has(n)) {
        return null;
      }
      visited.add(n);

      // Filter out disabled nodes (cached character templates) and their entire subtrees.
      // This prevents duplicate/mirrored bone hierarchies from appearing in the inspector.
      const enabled = typeof (n as any).isEnabled === 'function' ? (n as any).isEnabled() : true;
      if (enabled === false) {
        return null;
      }

      const id = String((n as any).uniqueId ?? n.id ?? n.name);
      nodeById.set(id, n);

      // Get children via normal parent-child relationships
      const children = (n as any).getChildren?.() as Node[] | undefined;

      const typed = nodeTypeName(n);
      const segment = `${typed}:${n.name}`;
      const path = parentPath ? `${parentPath}/${segment}` : segment;
      const isVisible = typeof (n as any).isVisible === 'boolean' ? (n as any).isVisible : undefined;

      const material = (n as any).material ? buildMaterialSummary((n as any).material) : null;
      const entityId = meshToEntity.get(id) || meshToEntity.get(n.name);

      return {
        id,
        path,
        name: n.name,
        type: typed,
        entityId,
        enabled,
        isVisible,
        material,
        children: (children ?? []).map((c) => toGraphNode(c, path)).filter((c): c is SceneGraphNode => c !== null),
      };
    };

    const graph: SceneGraphNode[] = scene.rootNodes
      .map((n) => toGraphNode(n, undefined))
      .filter((n): n is SceneGraphNode => n !== null);

    // Add Global Entities
    const levelData = levelBuilder.getLastLevelData();
    const globalEntitiesNode: SceneGraphNode = {
        id: 'global-entities',
        path: 'Global Entities',
        name: 'Global Entities',
        type: 'Folder',
        children: []
    };

    if (levelData?.spawnPoint) {
        globalEntitiesNode.children.push({
            id: 'spawn-point',
            path: 'Global Entities/Spawn Point',
            name: 'Spawn Point',
            type: 'SpawnPoint',
            entityId: 'spawn-point',
            children: []
        });
    }

    if (levelData?.mapConfig) {
        globalEntitiesNode.children.push({
            id: 'map-config',
            path: 'Global Entities/Map Config',
            name: 'Map Config',
            type: 'Config',
            entityId: 'map-config',
            children: []
        });
    }

    if (globalEntitiesNode.children.length > 0) {
        graph.unshift(globalEntitiesNode);
    }
    
    const pinned: PinnedSceneNode[] = [];
    for (const id of pinnedNodeIds) {
      const n = nodeById.get(id);
      if (!n) continue;
      const typed = (n as any).getClassName?.() ?? (n as any).constructor?.name ?? 'Node';
      const enabled = typeof (n as any).isEnabled === 'function' ? (n as any).isEnabled() : undefined;
      const isVisible = typeof (n as any).isVisible === 'boolean' ? (n as any).isVisible : undefined;
      const material = (n as any).material ? buildMaterialSummary((n as any).material) : null;
      pinned.push({ id, name: n.name, type: typed, enabled, isVisible, material });
    }

    let selected: SceneNodeDetails | null = null;
    if (selectedNodeId === 'spawn-point' && levelData?.spawnPoint) {
        selected = {
            id: 'spawn-point',
            name: 'Spawn Point',
            type: 'SpawnPoint',
            entityId: 'spawn-point',
            position: { x: levelData.spawnPoint.x, y: levelData.spawnPoint.y, z: levelData.spawnPoint.z },
            properties: {
                'Spawn Position': levelData.spawnPoint.toString()
            }
        };
    } else if (selectedNodeId === 'map-config' && levelData?.mapConfig) {
        selected = {
            id: 'map-config',
            name: 'Map Config',
            type: 'Config',
            entityId: 'map-config',
            properties: {
                'Gravity': levelData.mapConfig.physics?.gravity ? JSON.stringify(levelData.mapConfig.physics.gravity) : 'Default',
                'Ambient Color': levelData.mapConfig.ambient ? levelData.mapConfig.ambient.toString() : 'Default',
                'Camera Config': levelData.mapConfig.camera ? 'Present' : 'None'
            }
        };
    } else if (selectedNodeId) {
      const n = nodeById.get(selectedNodeId);
      if (n) {
        const typed = nodeTypeName(n);
        const enabled = typeof (n as any).isEnabled === 'function' ? (n as any).isEnabled() : undefined;
        const isVisible = typeof (n as any).isVisible === 'boolean' ? (n as any).isVisible : undefined;
        const parent = (n as any).parent as Node | null | undefined;
        const parentId = parent ? String((parent as any).uniqueId ?? parent.id ?? parent.name) : undefined;

        const v3 = (vec: any) =>
          vec && typeof vec.x === 'number' && typeof vec.y === 'number' && typeof vec.z === 'number'
            ? { x: vec.x, y: vec.y, z: vec.z }
            : undefined;

        const matObj = (n as any).material;
        let material = matObj ? buildMaterialSummary(matObj) : null;

        if (material && matObj) {
          const detailedTextures: Array<{ slot: string; name?: string; url?: string; previewUrl?: string }> = [];
          for (const slot of textureSlots) {
            const t = matObj[slot];
            if (!t) continue;
            const url = typeof t.url === 'string' ? t.url : undefined;
            const name = typeof t.name === 'string' ? t.name : undefined;
            const previewUrl = isValidPreviewUrl(url) ? url : await tryBuildTexturePreviewDataUrl(t);
            detailedTextures.push({ slot, name, url, previewUrl });
          }

          if (detailedTextures.length) {
            material = { ...material, textures: detailedTextures };
          }

          if (!isValidPreviewUrl(material.previewTextureUrl)) {
            const tex = pickFirstPreviewTexture(matObj);
            const previewDataUrl = await tryBuildTexturePreviewDataUrl(tex);
            if (previewDataUrl) {
              material = { ...material, previewTextureUrl: previewDataUrl };
            }
          }

          // Full material preview rendered on a sphere (preferred).
          const previewMat =
            matObj && Array.isArray((matObj as any).subMaterials)
              ? (matObj as any).subMaterials.find(Boolean)
              : matObj;
          const spherePreview = await buildMaterialPreviewDataUrl(previewMat);
          if (spherePreview) {
            material = { ...material, materialPreviewUrl: spherePreview };
          }
        }
        const vertices = typeof (n as any).getTotalVertices === 'function' ? (n as any).getTotalVertices() : undefined;
        const indicesArr = typeof (n as any).getIndices === 'function' ? (n as any).getIndices() : undefined;
        const indices = indicesArr ? (indicesArr.length ?? undefined) : undefined;
        const entityId = meshToEntity.get(selectedNodeId) || meshToEntity.get(n.name);

        // Collect light properties if this is a Light node
        let lightProperties: LightProperties | undefined;
        const isLight = typed.toLowerCase().includes('light');
        if (isLight) {
          const light = n as unknown as Light;
          const diffuse = light.diffuse;
          const specular = light.specular;
          lightProperties = {
            lightType: typed,
            enabled: light.isEnabled(),
            intensity: light.intensity,
            diffuseColor: { r: diffuse.r, g: diffuse.g, b: diffuse.b },
            specularColor: { r: specular.r, g: specular.g, b: specular.b },
          };
          // SpotLight / PointLight range
          if ('range' in light && typeof (light as any).range === 'number') {
            lightProperties.range = (light as any).range;
          }
          // SpotLight specific
          if ('angle' in light && typeof (light as any).angle === 'number') {
            lightProperties.angle = (light as any).angle;
          }
          if ('exponent' in light && typeof (light as any).exponent === 'number') {
            lightProperties.exponent = (light as any).exponent;
          }
          if ('innerAngle' in light && typeof (light as any).innerAngle === 'number') {
            lightProperties.innerAngle = (light as any).innerAngle;
          }
          // Check for shadow generator
          const shadowGen = light.getShadowGenerator?.();
          if (shadowGen) {
            lightProperties.shadowEnabled = true;
            lightProperties.shadowMapSize = shadowGen.getShadowMap()?.getSize().width;
          } else {
            lightProperties.shadowEnabled = false;
          }
        }

        // Check for godrays params
        let godRaysParams: { exposure: number; decay: number; weight: number; density: number } | undefined;
        if (postProcessManager.meshHasGodrays(n.name)) {
          godRaysParams = postProcessManager.getMeshGodRaysParams(n.name) ?? undefined;
        }

        // Check for custom material behavior (water, lava, video)
        let materialBehavior: SceneNodeDetails['materialBehavior'];
        const matBehaviorEntry = getMaterialBehaviorByMeshName(n.name);
        if (matBehaviorEntry) {
          materialBehavior = {
            behaviorId: matBehaviorEntry.behaviorId,
            displayName: matBehaviorEntry.displayName,
            entityId: matBehaviorEntry.entityId,
            params: matBehaviorEntry.getParams() as Record<string, number | string | boolean>,
          };
        }

        selected = {
          id: selectedNodeId,
          path: computeNodePath(n),
          name: n.name,
          type: typed,
          entityId,
          enabled,
          isVisible,
          parentId,
          position: v3((n as any).position),
          absolutePosition: (n as any).getAbsolutePosition ? v3((n as any).getAbsolutePosition()) : undefined,
          rotation: v3((n as any).rotation),
          scaling: v3((n as any).scaling),
          vertices,
          indices,
          material,
          lightProperties,
          godRaysParams,
          materialBehavior,
        };
      }
    }

    sceneGraphDirty = false;
    lastSceneGraphRefresh = Date.now();

    return { graph, pinned, selected };
};

// Check for dirty scene graph in render loop
scene.onBeforeRenderObservable.add(() => {
  if (isExplorerPanelVisible && sceneGraphDirty) {
    const now = Date.now();
    if (now - lastSceneGraphRefresh > SCENE_GRAPH_REFRESH_RATE) {
      doRefreshSceneGraph(lastPinnedNodeIds, lastSelectedNodeId).then(data => {
        debugUiBridge.emit({ type: 'scene:graph', ...data });
      });
    }
  }
  
  // Update lens DoF auto-focus on player
  postProcessManager.updateAutoFocus();
});

// Bridge between HUD and engine - MUST be created before HUD element is appended to DOM!
// The HUD dispatches panel state restoration commands in connectedCallback,
// so the bridge handlers must be ready to receive them.
const debugUiBridge = new DebugUiBridge(eventBus, {
  setDebugRendering: (enabled: boolean) => {
    debugRenderingEnabled = enabled;
    // Never show the HUD preview layer in the main cameras.
    applyMainCameraLayerMask();
    eventBus.emit('debug-render-toggled', { enabled: debugRenderingEnabled });
    // Also toggle trigger debug visualization
    triggerSystem.setDebugVisualization(enabled);
  },
  setSplineDebug: (enabled: boolean) => {
    if (enabled) {
      if (!splineDebugMesh && activeLoadedSpline) {
        splineDebugMesh = splineLoader.visualizeSpline(activeLoadedSpline, 'debugSpline', new Color3(1, 0, 1));
      }
      if (splineDebugMesh) splineDebugMesh.isVisible = true;
    } else {
      if (splineDebugMesh) splineDebugMesh.isVisible = false;
    }
  },
  setPlayerDebug: (enabled: boolean) => {
    if (playerController) {
      playerController.setDebugEnabled(enabled);
      playerController.setDebugMeshVisibility(enabled);
    }
  },
  setWeaponMountDebug: (enabled: boolean) => {
    if (playerController) playerController.setWeaponMountDebugVisibility(enabled);
  },
  setCombatDebug: (enabled: boolean) => {
    combatDebugEnabled = enabled;
    combatSystem.setDebugEnabled(enabled);
  },
  setCollisionDebug: (enabled: boolean) => {
    // Clear existing debug meshes
    collisionDebugMeshes.forEach(m => m.dispose());
    collisionDebugMeshes = [];
    
    if (enabled) {
      // Create wireframe copies of all collision meshes
      scene.meshes.forEach(mesh => {
        // Check if this mesh has a physics body via PhysicsManager (registered at runtime)
        // or has the hm_collision annotation from Blender export
        const hasPhysicsBody = mesh.metadata?.hasPhysicsBody === true;
        const hasCollisionAnnotation = mesh.metadata?.gltf?.extras?.hm_collision === true ||
                                       mesh.metadata?.hm_collision === true ||
                                       (mesh as any)._hm_collision === true;
        
        // Check if this is a character mesh via EntityRegistry
        const entityInfo = entityRegistry.getByMesh(mesh);
        const isCharacterMesh = entityInfo?.type === EntityType.CHARACTER;
        
        if ((hasPhysicsBody || hasCollisionAnnotation || isCharacterMesh) && mesh.getTotalVertices() > 0) {
          try {
            // Create wireframe clone
            const debugMesh = mesh.clone(mesh.name + '_debug_collision', null) as Mesh;
            if (!debugMesh) return;
            
            // Copy world matrix to position it correctly
            debugMesh.position = mesh.absolutePosition.clone();
            debugMesh.rotation = mesh.rotation.clone();
            debugMesh.scaling = mesh.absoluteScaling.clone();
            
            // Make it visible wireframe
            debugMesh.isVisible = true;
            debugMesh.isPickable = false;
            debugMesh.checkCollisions = false;
            
            // Create debug material
            const mat = new StandardMaterial(mesh.name + '_debug_mat', scene);
            mat.wireframe = true;
            mat.emissiveColor = new Color3(1, 0.5, 0); // Orange for collision
            mat.disableLighting = true;
            debugMesh.material = mat;
            
            // Put on debug layer
            debugMesh.layerMask = DEBUG_LAYER_MASK;
            
            collisionDebugMeshes.push(debugMesh);
          } catch (e) {
            // Skip meshes that can't be cloned
          }
        }
      });
      logger.core.log(`[Collision Debug] Created ${collisionDebugMeshes.length} debug meshes`);
    }
  },
  setLightDebug: (enabled: boolean) => {
    lightDebugEnabled = enabled;
    
    // Clear existing debug meshes and mapping
    lightDebugMeshes.forEach(m => m.dispose());
    lightDebugMeshes = [];
    lightDebugMeshMap.clear();
    
    if (enabled) {
      scene.lights.forEach(light => {
        // Check if light has a position property (not all lights do)
        const lightWithPos = light as any;
        if (!lightWithPos.position) return; // Skip lights without position (like hemispheric)
        
        const pos = (lightWithPos.position as Vector3).clone();
        const lightType = light.getClassName();
        const isEnabled = light.isEnabled();
        
        // Create a sphere to represent the light position
        const sphere = MeshBuilder.CreateSphere(
          `debug_light_${light.name}`,
          { diameter: 0.5, segments: 8 },
          scene
        );
        sphere.position = pos;
        sphere.isPickable = false;
        sphere.checkCollisions = false;
        
        // Create emissive material with light color
        const mat = new StandardMaterial(`debug_light_mat_${light.name}`, scene);
        mat.emissiveColor = light.diffuse?.clone() || new Color3(1, 1, 1);
        mat.disableLighting = true;
        mat.alpha = isEnabled ? 1.0 : 0.3;
        sphere.material = mat;
        sphere.layerMask = DEBUG_LAYER_MASK;
        
        lightDebugMeshes.push(sphere);
        // Track mapping for dynamic updates
        lightDebugMeshMap.set(sphere.name, light.name);
        
        // For spotlights, add a cone showing direction
        if (lightType === 'SpotLight' && (light as any).direction) {
          const spotLight = light as any;
          const coneHeight = spotLight.range ? Math.min(spotLight.range, 10) : 5;
          const coneRadius = Math.tan(spotLight.angle || 0.8) * coneHeight;
          
          const cone = MeshBuilder.CreateCylinder(
            `debug_light_cone_${light.name}`,
            { diameterTop: 0, diameterBottom: coneRadius * 2, height: coneHeight, tessellation: 16 },
            scene
          );
          
          // Position and orient the cone
          cone.position = pos.clone();
          
          // Rotate to align with light direction
          const dir = spotLight.direction.normalize();
          // Cylinder points along Y axis, rotate to match direction
          const up = new Vector3(0, 1, 0);
          const angle = Math.acos(Vector3.Dot(up, dir));
          const axis = Vector3.Cross(up, dir).normalize();
          if (axis.length() > 0.001) {
            cone.rotationQuaternion = Quaternion.RotationAxis(axis, angle);
          }
          // Move cone so tip is at light position
          cone.position.addInPlace(dir.scale(coneHeight / 2));
          
          cone.isPickable = false;
          cone.checkCollisions = false;
          
          const coneMat = new StandardMaterial(`debug_light_cone_mat_${light.name}`, scene);
          coneMat.emissiveColor = light.diffuse?.clone() || new Color3(1, 1, 1);
          coneMat.disableLighting = true;
          coneMat.alpha = 0.3;
          coneMat.wireframe = true;
          cone.material = coneMat;
          cone.layerMask = DEBUG_LAYER_MASK;
          
          lightDebugMeshes.push(cone);
        }
        
        // For point lights with position at origin, show with extra indicator
        if (pos.length() < 0.1) {
          sphere.scaling.scaleInPlace(2); // Make it bigger so it's visible
          logger.core.warn(`[Light Debug] Light '${light.name}' at origin (0,0,0) - may be incorrectly positioned`);
        }
        
        logger.core.log(`[Light Debug] ${lightType} '${light.name}' pos=(${pos.x.toFixed(1)}, ${pos.y.toFixed(1)}, ${pos.z.toFixed(1)}) intensity=${light.intensity.toFixed(1)} enabled=${isEnabled}`);
      });
      logger.core.log(`[Light Debug] Created ${lightDebugMeshes.length} debug meshes for ${scene.lights.length} lights`);
    }
  },
  setPixelation: (enabled: boolean) => {
    pixelationEnabled = enabled;
    postProcessManager.togglePixelation(enabled);
    debugUiBridge.emit({ type: 'postprocess:pixelation:changed', enabled });
  },
  setPixelSize: (value: number) => {
    if (!animatePixelation) {
      postProcessManager.pixelateSizeX = value;
      postProcessManager.pixelateSizeY = value;
    }
    debugUiBridge.emit({ type: 'postprocess:pixelSize:changed', value });
  },
  setPixelAnimate: (enabled: boolean) => {
    animatePixelation = enabled;
    debugUiBridge.emit({ type: 'postprocess:pixelAnimate:changed', enabled });
  },
  setGodRays: (enabled: boolean) => {
    postProcessManager.toggleGodRays(enabled);
    debugUiBridge.emit({ type: 'postprocess:godRays:changed', enabled });
  },
  setMeshGodRaysExposure: (meshName: string, value: number) => {
    postProcessManager.setMeshGodRaysExposure(meshName, value);
    const params = postProcessManager.getMeshGodRaysParams(meshName);
    if (params) {
      debugUiBridge.emit({ type: 'postprocess:meshGodRays:params:changed', meshName, ...params });
    }
  },
  setMeshGodRaysDecay: (meshName: string, value: number) => {
    postProcessManager.setMeshGodRaysDecay(meshName, value);
    const params = postProcessManager.getMeshGodRaysParams(meshName);
    if (params) {
      debugUiBridge.emit({ type: 'postprocess:meshGodRays:params:changed', meshName, ...params });
    }
  },
  setMeshGodRaysWeight: (meshName: string, value: number) => {
    postProcessManager.setMeshGodRaysWeight(meshName, value);
    const params = postProcessManager.getMeshGodRaysParams(meshName);
    if (params) {
      debugUiBridge.emit({ type: 'postprocess:meshGodRays:params:changed', meshName, ...params });
    }
  },
  setMeshGodRaysDensity: (meshName: string, value: number) => {
    postProcessManager.setMeshGodRaysDensity(meshName, value);
    const params = postProcessManager.getMeshGodRaysParams(meshName);
    if (params) {
      debugUiBridge.emit({ type: 'postprocess:meshGodRays:params:changed', meshName, ...params });
    }
  },
  setChromaticAberration: (enabled: boolean) => {
    postProcessManager.toggleChromaticAberration(enabled);
    debugUiBridge.emit({ type: 'postprocess:chromaticAberration:changed', enabled });
  },
  setChromaticAberrationAmount: (value: number) => {
    postProcessManager.setChromaticAberrationAmount(value);
    debugUiBridge.emit({ type: 'postprocess:chromaticAberration:amount:changed', value });
  },
  setChromaticAberrationRadialIntensity: (value: number) => {
    postProcessManager.setChromaticAberrationRadialIntensity(value);
    debugUiBridge.emit({ type: 'postprocess:chromaticAberration:radialIntensity:changed', value });
  },
  
  // Lens effect handlers
  setLensEffect: (enabled: boolean) => {
    postProcessManager.toggleLensEffect(enabled);
    debugUiBridge.emit({ type: 'postprocess:lens:changed', enabled });
    if (enabled) {
      // Send current config to UI
      const config = postProcessManager.getLensConfig();
      debugUiBridge.emit({ type: 'postprocess:lens:config:changed', config });
    }
  },
  setLensEdgeBlur: (value: number) => {
    postProcessManager.setLensEdgeBlur(value);
  },
  setLensChromaticAberration: (value: number) => {
    postProcessManager.setLensConfig({ chromatic_aberration: value });
  },
  setLensDistortion: (value: number) => {
    postProcessManager.setLensConfig({ distortion: value });
  },
  setLensGrain: (value: number) => {
    postProcessManager.setLensGrainAmount(value);
  },
  setLensDofDistance: (value: number) => {
    postProcessManager.setLensFocusDistance(value);
  },
  setLensDofAperture: (value: number) => {
    postProcessManager.setLensAperture(value);
  },
  setLensDofGain: (value: number) => {
    postProcessManager.setLensConfig({ dof_gain: value });
  },
  setLensDofAutoFocus: (enabled: boolean) => {
    postProcessManager.setDofAutoFocus(enabled);
    debugUiBridge.emit({ type: 'postprocess:lens:dofAutoFocus:changed', enabled });
  },
  setLensDofEnabled: (enabled: boolean) => {
    postProcessManager.setDofEnabled(enabled);
    debugUiBridge.emit({ type: 'postprocess:lens:dofEnabled:changed', enabled });
  },
  
  // Fog handlers
  setFogEnabled: (enabled: boolean) => {
    environmentSettings.setFogEnabled(enabled);
    debugUiBridge.emit({ 
      type: 'environment:fog:changed', 
      enabled, 
      density: environmentSettings.getFogDensity(),
      color: {
        r: environmentSettings.getFogColor().r,
        g: environmentSettings.getFogColor().g,
        b: environmentSettings.getFogColor().b
      }
    });
  },
  setFogDensity: (value: number) => {
    environmentSettings.setFogDensity(value);
    debugUiBridge.emit({ 
      type: 'environment:fog:changed', 
      enabled: environmentSettings.getFogEnabled(), 
      density: value,
      color: {
        r: environmentSettings.getFogColor().r,
        g: environmentSettings.getFogColor().g,
        b: environmentSettings.getFogColor().b
      }
    });
  },
  setFogColor: (r: number, g: number, b: number) => {
    environmentSettings.setFogColor(r, g, b);
    debugUiBridge.emit({ 
      type: 'environment:fog:changed', 
      enabled: environmentSettings.getFogEnabled(), 
      density: environmentSettings.getFogDensity(),
      color: { r, g, b }
    });
  },
  
  // Ambient color handler
  setAmbientColor: (r: number, g: number, b: number) => {
    environmentSettings.setAmbientColor(r, g, b);
    debugUiBridge.emit({ 
      type: 'environment:ambient:changed', 
      color: { r, g, b }
    });
  },

  // HDR environment handlers
  loadHdr: (url: string, envIntensity?: number, skyboxBrightness?: number) => {
    levelBuilder.loadHdrFromUrl(url, envIntensity, skyboxBrightness);
    const envInfo = levelBuilder.getEnvironmentInfo();
    debugUiBridge.emit({
      type: 'environment:hdr:changed',
      url: envInfo.hdrUrl ?? null,
      envIntensity: envInfo.envIntensity ?? 1.0,
      skyboxBrightness: envInfo.skyboxBrightness ?? 0.5
    });
  },
  setEnvIntensity: (value: number) => {
    levelBuilder.setEnvIntensity(value);
    const envInfo = levelBuilder.getEnvironmentInfo();
    debugUiBridge.emit({
      type: 'environment:hdr:changed',
      url: envInfo.hdrUrl ?? null,
      envIntensity: value,
      skyboxBrightness: envInfo.skyboxBrightness ?? 0.5
    });
  },
  setSkyboxBrightness: (value: number) => {
    levelBuilder.setSkyboxBrightness(value);
    const envInfo = levelBuilder.getEnvironmentInfo();
    debugUiBridge.emit({
      type: 'environment:hdr:changed',
      url: envInfo.hdrUrl ?? null,
      envIntensity: envInfo.envIntensity ?? 1.0,
      skyboxBrightness: value
    });
  },
  
  setFullBright: (enabled: boolean) => {
    environmentSettings.setFullBrightMode(enabled);
    debugUiBridge.emit({ type: 'lighting:fullBright:changed', enabled });
  },
  setLightGizmos: (enabled: boolean) => {
    // Reuse existing light debug functionality
    lightDebugEnabled = enabled;
    
    // Clear existing debug meshes and mapping
    lightDebugMeshes.forEach(m => m.dispose());
    lightDebugMeshes = [];
    lightDebugMeshMap.clear();
    
    if (enabled) {
      scene.lights.forEach(light => {
        // Check if light has a position property (not all lights do)
        const lightWithPos = light as any;
        if (!lightWithPos.position) return; // Skip lights without position (like hemispheric)
        
        const pos = (lightWithPos.position as Vector3).clone();
        const isEnabled = light.isEnabled();
        
        // Create a sphere to represent the light position
        const sphere = MeshBuilder.CreateSphere(
          `debug_light_${light.name}`,
          { diameter: 0.5, segments: 8 },
          scene
        );
        sphere.position = pos;
        sphere.isPickable = false;
        sphere.checkCollisions = false;
        
        // Create emissive material with light color
        const mat = new StandardMaterial(`debug_light_mat_${light.name}`, scene);
        mat.emissiveColor = light.diffuse?.clone() || new Color3(1, 1, 1);
        mat.disableLighting = true;
        mat.alpha = isEnabled ? 1.0 : 0.3;
        sphere.material = mat;
        sphere.layerMask = DEBUG_LAYER_MASK;
        
        lightDebugMeshes.push(sphere);
        lightDebugMeshMap.set(sphere.name, light.name);
      });
    }
    
    debugUiBridge.emit({ type: 'lighting:gizmos:changed', enabled });
  },
  setLightHighlight: (lightName: string | null) => {
    // Highlight a light gizmo in the scene
    if (!lightName) {
      // Clear highlight
      if (highlightMesh) highlightMesh.isVisible = false;
      return;
    }
    
    // Find the debug mesh for this light
    const debugMeshName = `debug_light_${lightName}`;
    const debugMesh = lightDebugMeshes.find(m => m.name === debugMeshName);
    if (!debugMesh) {
      if (highlightMesh) highlightMesh.isVisible = false;
      return;
    }
    
    // Create highlight mesh if needed
    if (!highlightMesh) {
      highlightMesh = MeshBuilder.CreateBox('debug_highlight', { size: 1 }, scene);
      highlightMesh.isPickable = false;
      const mat = new StandardMaterial('debug_highlight_mat', scene);
      mat.emissiveColor = new Color3(0, 1, 0);
      mat.wireframe = true;
      mat.disableLighting = true;
      highlightMesh.material = mat;
      highlightMesh.renderingGroupId = 3;
    }
    
    highlightMesh.isVisible = true;
    highlightMesh.position.copyFrom(debugMesh.position);
    highlightMesh.scaling.setAll(1.0);
  },
  highlightNode: (nodeId: string | null) => {
    if (!highlightMesh) {
      highlightMesh = MeshBuilder.CreateBox('debug_highlight', { size: 1 }, scene);
      highlightMesh.isPickable = false;
      const mat = new StandardMaterial('debug_highlight_mat', scene);
      mat.emissiveColor = new Color3(0, 1, 0);
      mat.wireframe = true;
      mat.disableLighting = true;
      highlightMesh.material = mat;
      highlightMesh.renderingGroupId = 3; // Draw on top
    }

    if (!nodeId) {
      highlightMesh.isVisible = false;
      return;
    }

    let node: Node | null = null;
    const numId = parseInt(nodeId);
    if (!isNaN(numId)) {
        node = scene.getMeshByUniqueId(numId) || scene.getTransformNodeByUniqueId(numId);
    }
    if (!node) {
        node = scene.getNodeById(nodeId) || scene.getMeshByName(nodeId) || scene.getTransformNodeByName(nodeId);
    }

    if (!node || !(node as any).getAbsolutePosition) {
      highlightMesh.isVisible = false;
      return;
    }

    // Don't highlight water meshes - interferes with WaterMaterial rendering
    if ((node as any).material?.getClassName?.() === 'WaterMaterial') {
      highlightMesh.isVisible = false;
      return;
    }

    highlightMesh.isVisible = true;
    highlightMesh.rotationQuaternion = null;
    highlightMesh.rotation.setAll(0);

    // Prefer world-space bounds for correct placement/size.
    // Using the node pivot (`getAbsolutePosition`) plus local `extendSize` can look offset.
    if ((node as any).getBoundingInfo) {
      const bi = (node as any).getBoundingInfo();
      const centerWorld = bi.boundingBox.centerWorld;
      const extendWorld = bi.boundingBox.extendSizeWorld;
      highlightMesh.position.copyFrom(centerWorld);
      highlightMesh.scaling.set(
        Math.max(extendWorld.x * 2.1, 0.2),
        Math.max(extendWorld.y * 2.1, 0.2),
        Math.max(extendWorld.z * 2.1, 0.2)
      );
      return;
    }

    // For TransformNodes (bones, etc): compute aggregate bounds from descendant meshes.
    if (node instanceof TransformNode) {
      const childMeshes = node.getChildMeshes(false, (n) => {
        // Only include enabled, visible meshes with bounds
        return n.isEnabled() && n.isVisible && !!(n as any).getBoundingInfo;
      });

      if (childMeshes.length > 0) {
        let minX = Infinity, minY = Infinity, minZ = Infinity;
        let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

        for (const mesh of childMeshes) {
          const bi = (mesh as any).getBoundingInfo();
          const min = bi.boundingBox.minimumWorld;
          const max = bi.boundingBox.maximumWorld;
          
          minX = Math.min(minX, min.x);
          minY = Math.min(minY, min.y);
          minZ = Math.min(minZ, min.z);
          maxX = Math.max(maxX, max.x);
          maxY = Math.max(maxY, max.y);
          maxZ = Math.max(maxZ, max.z);
        }

        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;
        const centerZ = (minZ + maxZ) / 2;
        const sizeX = maxX - minX;
        const sizeY = maxY - minY;
        const sizeZ = maxZ - minZ;

        highlightMesh.position.set(centerX, centerY, centerZ);
        highlightMesh.scaling.set(
          Math.max(sizeX * 1.05, 0.2),
          Math.max(sizeY * 1.05, 0.2),
          Math.max(sizeZ * 1.05, 0.2)
        );
        return;
      }
    }

    // Fallback: no bounds available and no child meshes. Use pivot with small box.
    const pos = (node as any).getAbsolutePosition();
    highlightMesh.position.copyFrom(pos);
    highlightMesh.scaling.setAll(0.5);
  },
  setScenePickMode: (enabled: boolean) => {
    scenePickModeEnabled = enabled;

    if (!enabled) {
      if (scenePickObserver) {
        scene.onPointerObservable.remove(scenePickObserver);
        scenePickObserver = null;
      }
      return;
    }

    if (scenePickObserver) return;
    scenePickObserver = scene.onPointerObservable.add((pi) => {
      if (!scenePickModeEnabled) return;
      if (pi.type !== PointerEventTypes.POINTERDOWN) return;
      const evt = pi.event as PointerEvent;
      if (evt.button !== 0) return;

      const pick = scene.pick(scene.pointerX, scene.pointerY);
      const picked = pick?.pickedMesh ?? null;
      const selectedNodeId = picked ? String((picked as any).uniqueId ?? picked.id ?? picked.name) : null;

      // Disable pick mode after a selection attempt to avoid accidental repeat picks.
      scenePickModeEnabled = false;
      if (scenePickObserver) {
        scene.onPointerObservable.remove(scenePickObserver);
        scenePickObserver = null;
      }

      debugUiBridge.emit({ type: 'scene:pick', selectedNodeId });
    });
  },
  setExplorerVisibility: (visible: boolean) => {
    isExplorerPanelVisible = visible;
    if (visible && sceneGraphDirty) {
        doRefreshSceneGraph(lastPinnedNodeIds, lastSelectedNodeId).then(data => {
            debugUiBridge.emit({ type: 'scene:graph', ...data });
        });
    }
  },
  refreshSceneGraph: async (pinnedNodeIds: string[], selectedNodeId?: string | null) => {
    lastPinnedNodeIds = pinnedNodeIds;
    lastSelectedNodeId = selectedNodeId;
    isExplorerPanelVisible = true; // Assume visible if requesting refresh
    const data = await doRefreshSceneGraph(pinnedNodeIds, selectedNodeId);
    debugUiBridge.emit({ type: 'scene:graph', ...data });
  },
  toggleTimeline: () => {
    // Timeline handled by debug HUD panels now
  },
  setTimeline: (_enabled: boolean) => {
    // Timeline handled by debug HUD panels now
  },
  refreshEnvironment: () => {
    debugUiBridge.emit({ type: 'environment:info', info: buildEnvironmentInfo() });
  },
  resetHud: () => {
    // no-op engine side for now
  },
  changeMap: async (mapPath: string) => {
    await changeMap(mapPath);
  },
  getCameras: () => {
    const getCameraWorldPosition = (cam: Camera): Vector3 => {
      try {
        cam.computeWorldMatrix();
      } catch {
        // ignore
      }
      const anyCam = cam as any;
      if (typeof anyCam.getAbsolutePosition === 'function') return anyCam.getAbsolutePosition();
      if (anyCam.globalPosition) return anyCam.globalPosition as Vector3;
      return cam.position;
    };

    const getCameraTarget = (cam: Camera): Vector3 | null => {
      const anyCam = cam as any;
      if (typeof anyCam.getTarget === 'function') return anyCam.getTarget();
      return anyCam.target ?? null;
    };

    const cameras = scene.cameras.map(cam => {
      const isMapCamera = cam !== camera && cam !== freeRoamCamera && cam.name !== 'hud_material_preview_camera';
      const position = getCameraWorldPosition(cam);
      const result: any = {
        name: cam.name,
        position: { x: position.x, y: position.y, z: position.z },
        type: cam.getClassName?.() || 'Camera',
        isMapCamera
      };
      
      const target = getCameraTarget(cam);
      if (target) result.target = { x: target.x, y: target.y, z: target.z };
      
      return result;
    });
    
    debugUiBridge.emit({ type: 'cameras:list', cameras });
  },
  jumpToCamera: (cameraName: string) => {
    const targetCamera = scene.cameras.find(c => c.name === cameraName);
    if (!targetCamera) {
      logger.camera.warn(`Camera '${cameraName}' not found`);
      return;
    }
    
    let targetPos: Vector3;
    try {
      targetCamera.computeWorldMatrix();
    } catch {
      // ignore
    }
    const anyCam = targetCamera as any;
    if (typeof anyCam.getAbsolutePosition === 'function') targetPos = anyCam.getAbsolutePosition();
    else targetPos = (anyCam.globalPosition as Vector3) || targetCamera.position;

    const targetTarget: Vector3 | null =
      typeof anyCam.getTarget === 'function' ? anyCam.getTarget() : (anyCam.target ?? null);
    
    // Switch to free roam camera and position it
    if (freeRoamCamera) {
      freeRoamCamera.position.copyFrom(targetPos);
      
      if (targetTarget) {
        const direction = targetTarget.subtract(targetPos);
        const rotationMatrix = Matrix.Identity();
        Matrix.LookAtLHToRef(Vector3.Zero(), direction, Vector3.Up(), rotationMatrix);
        const quaternion = Quaternion.FromRotationMatrix(rotationMatrix);
        freeRoamCamera.rotation = quaternion.toEulerAngles();
      }
      
      scene.activeCamera = freeRoamCamera;
      logger.camera.log(`📷 Jumped to camera '${cameraName}' at (${targetPos.x.toFixed(2)}, ${targetPos.y.toFixed(2)}, ${targetPos.z.toFixed(2)})`);
    }
  },
  toggleCameraDebugVisibility: (visible: boolean) => {
    // This will be handled by storing state and re-rendering cameras
    // For now, just log
    logger.camera.log(`🎥 Camera debug visibility: ${visible}`);
  },
  getCharacters: () => {
    const characters = partyCharacters
      .filter(c => c !== null)
      .map(char => {
        const hp = char.getHp();
        const pos = char.character.position;
        const vel = char.velocity;
        
        return {
          id: char.getId(),
          hp: { current: hp.current, max: hp.max },
          position: { x: pos.x, y: pos.y, z: pos.z },
          velocity: { x: vel.x, y: vel.y, z: vel.z },
          isGrounded: char.isGrounded,
          playerT: char.playerT,
          facingDirection: char.getFacingDirection(),
        };
      });
    
    debugUiBridge.emit({ type: 'characters:list', characters });
  },
  // Impact effects handlers
  triggerImpactPreset: (preset: string) => {
    const presets = ImpactPresets as Record<string, { ripple: any; shake: any }>;
    const presetConfig = presets[preset];
    if (!presetConfig) {
      logger.debug.warn(`Unknown impact preset: ${preset}`);
      return;
    }
    
    // Trigger ripple if defined
    if (presetConfig.ripple) {
      impactEffectManager.triggerRipple({
        centerX: 0.5,
        centerY: 0.5,
        ...presetConfig.ripple,
      });
    }
    
    // Trigger shake if defined
    if (presetConfig.shake) {
      impactEffectManager.triggerShake(presetConfig.shake);
    }
    
    logger.debug.log(`🎬 Triggered impact preset: ${preset}`);
  },
  triggerRipple: (config: { centerX: number; centerY: number; amplitude?: number; duration?: number; frequency?: number; speed?: number; timeRamp?: string }) => {
    impactEffectManager.triggerRipple({
      centerX: config.centerX,
      centerY: config.centerY,
      amplitude: config.amplitude,
      duration: config.duration,
      frequency: config.frequency,
      speed: config.speed,
      timeRamp: config.timeRamp as any,
    });
  },
  triggerShake: (config: { amplitude?: number; duration?: number; frequency?: number; zoomAmount?: number; timeRamp?: string }) => {
    impactEffectManager.triggerShake({
      amplitude: config.amplitude,
      duration: config.duration,
      frequency: config.frequency,
      zoomAmount: config.zoomAmount,
      timeRamp: config.timeRamp as any,
    });
  },
  cancelAllImpactEffects: () => {
    impactEffectManager.cancelAll();
    logger.debug.log(`❌ Cancelled all impact effects`);
  },
  updateLight: (nodeId: string, property: string, value: number | boolean | { r: number; g: number; b: number }) => {
    // Find the light by nodeId (uniqueId)
    const light = scene.lights.find(l => String(l.uniqueId) === nodeId || l.name === nodeId);
    if (!light) {
      logger.debug.warn(`Light not found: ${nodeId}`);
      return;
    }
    
    switch (property) {
      case 'enabled':
        light.setEnabled(value as boolean);
        break;
      case 'intensity':
        light.intensity = value as number;
        break;
      case 'diffuseColor':
        if (typeof value === 'object' && 'r' in value) {
          light.diffuse = new Color3(value.r, value.g, value.b);
        }
        break;
      case 'specularColor':
        if (typeof value === 'object' && 'r' in value) {
          light.specular = new Color3(value.r, value.g, value.b);
        }
        break;
      case 'range':
        if ('range' in light) {
          (light as any).range = value as number;
        }
        break;
      case 'angle':
        if ('angle' in light) {
          (light as any).angle = value as number;
        }
        break;
      case 'exponent':
        if ('exponent' in light) {
          (light as any).exponent = value as number;
        }
        break;
      case 'innerAngle':
        if ('innerAngle' in light) {
          (light as any).innerAngle = value as number;
        }
        break;
      default:
        logger.debug.warn(`Unknown light property: ${property}`);
    }
    
    logger.debug.log(`💡 Updated light ${light.name} ${property}=${JSON.stringify(value)}`);
    sceneGraphDirty = true;
  },
  setCullingEnabled: (enabled: boolean) => {
    const culling = ServiceRegistry.getSpatialCulling();
    if (culling) {
      culling.setEnabled(enabled);
      logger.debug.log(`📐 Spatial culling ${enabled ? 'enabled' : 'disabled'}`);
    }
  },
  setCullingDistance: (value: number) => {
    const culling = ServiceRegistry.getSpatialCulling();
    if (culling) {
      culling.setCullDistance(value);
      logger.debug.log(`📐 Cull distance set to ${value}m`);
    }
  },
  setFrustumFarPlane: (value: number) => {
    const culling = ServiceRegistry.getSpatialCulling();
    if (culling) {
      culling.setFrustumFarPlane(value);
      logger.debug.log(`📐 Frustum far plane set to ${value}m`);
    }
  },
  
  // Behavior editor handlers
  refreshBehaviors: () => {
    const entities = collectBehaviorEntities(scene);
    debugUiBridge.emit({ type: 'behaviors:list', entities });
  },
  updateBehaviorParam: (entityId: string, paramName: string, value: number | string | boolean) => {
    updateBehaviorParameter(scene, entityId, paramName, value);
  },
  
  // Material behavior handler (explorer panel)
  updateMaterialBehaviorParam: (entityId: string, paramName: string, value: number | string | boolean) => {
    const entry = getMaterialBehavior(entityId);
    if (entry) {
      entry.setParam(paramName, value);
      logger.debug.log(`🎨 Material ${entityId}.${paramName} = ${value}`);
    } else {
      logger.debug.warn(`⚠️ Material behavior not found for entity: ${entityId}`);
    }
  },
});

// Now that bridge is ready, create and append the HUD.
// The HUD's connectedCallback will dispatch panel state restoration commands,
// which will now be properly handled by the bridge.
const debugHudEl = document.createElement('himum-debug-hud');
debugHudEl.setAttribute('aria-hidden', 'true');
document.body.appendChild(debugHudEl);

ensureMapDropdown();

// Initialize Environment Settings
const environmentSettings = new EnvironmentSettings(scene);

// Initialize Dynamic Object System
const dynamicObjectSystem = new DynamicObjectSystem(scene, physicsManager, entityRegistry, particleManager, eventBus);
dynamicObjectSystem.initialize(StateManager.getInstance());
logger.core.log('✅ Physics systems initialized (zones, dynamic objects)');

// Expose StateManager and CombatSystem for debugging
(window as any).__stateManager = StateManager.getInstance();
(window as any).__combatSystem = combatSystem;
(window as any).__scene = scene;
(window as any).__physicsManager = physicsManager;
(window as any).__entityRegistry = entityRegistry;
(window as any).__particleManager = particleManager;
(window as any).__dynamicObjectSystem = dynamicObjectSystem;

// Initialize ServiceRegistry with all dependencies
ServiceRegistry.initialize({
  scene,
  physics: physicsManager,
  particles: particleManager,
  camera: cameraController,
  entityRegistry
});

// Initialize FX Manager and wire to camera controller
const fxManager = new FXManager(scene, camera, particleManager);
cameraController.setShakeEffect(fxManager.getCameraShakeEffect());
ServiceRegistry.setFXManager(fxManager);
logger.core.log('✅ FXManager initialized');

// Initialize Level
const levelBuilder = new LevelBuilder(scene, physicsManager, triggerSystem, postProcessManager, particleManager, dynamicObjectSystem, StateManager.getInstance());

// Level Configuration - check localStorage for saved map first
async function initGame() {
  // Start loading progress tracking (1 map + 4 characters)
  loadingManager.setTotal(5);
  
  // Load configs
  const danielConfig = await CharacterConfigLoader.load('daniel');
  const lillyConfig = await CharacterConfigLoader.load('lilly');
  const hotdogConfig = await CharacterConfigLoader.load('hotdog');
  const lavazombieConfig = await CharacterConfigLoader.load('lavazombie');
  
  // NOTE: Hotdog is now a world NPC, spawned via NPCSpawnManager from map markers
  partyMembers = [
    { id: 'lilly', config: lillyConfig },
    { id: 'daniel', config: danielConfig },
  ];

  // Initialize Leader Character (will be loaded with cached asset later)
  playerController = new NPCController(scene, splineConstraint, partyMembers[0].config, `${partyMembers[0].id}_party_0`, physicsManager);
playerController.enablePlayerControl(input, cameraController);
playerController.setCombatSystem(combatSystem);
playerController.setPlayerControlled(true);
partyCharacters[0] = playerController;

// Initialize Party Manager for followers
partyManager = new PartyManager(scene, splineConstraint, physicsManager);

// Initialize NPC Spawn Manager for world NPCs
npcSpawnManager = new NPCSpawnManager(
  scene,
  splineConstraint,
  physicsManager,
  entityRegistry,
  characterCache,
  eventBus,
  combatSystem
);

// NOTE: Test triggers removed - triggers now require mesh geometry or bounding box.
// Real triggers are loaded from Blender maps via TriggerSystem.registerFromMesh()

// Register trigger event handlers
eventBus.on('trigger:enter', (data) => {
  const entityInfo = entityRegistry.getEntityInfo(data.triggerId);
  logger.triggers.log('Trigger entered:', data.triggerId, entityInfo?.metadata);
});

eventBus.on('trigger:exit', (data) => {
  logger.triggers.debug('Trigger exited:', data.triggerId);
});

eventBus.on('trigger:camera-change', (data) => {
    logger.camera.log('📷 Camera Trigger:', data.triggerName, '->', data.data.cameraType);
    
    // Track active camera trigger for debug UI
    activeCameraTrigger = data.triggerName || null;
    
    // Emit updated environment info to debug UI
    debugUiBridge.emit({ type: 'environment:info', info: buildEnvironmentInfo() });
    
    // Fallback: If trigger data is missing spline (e.g. level load failed), use active spline
    const cameraData = data.data.cameraData;
    if ((data.data.cameraType === 'follow-player' || data.data.cameraType === 'follow-side') && !cameraData.spline) {
         logger.camera.warn('Camera trigger missing spline, using active gameplay spline');
         cameraData.spline = splineConstraint.getSpline();
    }

    if (cameraController) {
        cameraController.setCameraMode(
            data.data.cameraType,
            cameraData,
            playerController.playerT,
            1.5 // Transition duration
        );
    }
});

// Main initialization function
async function initLevelAndPhysics() {
  // Initialize physics
  await physicsManager.initialize(new Vector3(0, -30, 0));
  logger.physics.log('Physics initialized');

  // Build level
  const { spline: loadedSpline, spawnPoint: _spawnPoint, splineSource, mapConfig, npcSpawns, cameraAnimations } = await levelBuilder.build(levelConfig);
  loadingManager.reportLoaded('map');
  logger.level.log('Level built');
  
  // Emit initial godrays state to debug UI after level load
  debugUiBridge.emit({ 
    type: 'postprocess:godRays:changed', 
    enabled: postProcessManager.isGodRaysEnabled() 
  });
  
  // Initialize CinematicManager with camera animations from map
  CinematicManager.initialize(scene, cameraController);
  if (cameraAnimations.length > 0) {
    CinematicManager.registerAnimations(cameraAnimations);
  }
  
  // Set game camera for spatial culling (so frustum checks use follow camera, not free-roam)
  const spatialCullingForCamera = ServiceRegistry.getSpatialCulling();
  if (spatialCullingForCamera) {
    spatialCullingForCamera.setGameCamera(camera);
  }
  
  // === CAMERA SETUP ===
  // Find the start camera (specified in scene config, or first scene camera as fallback)
  let derivedCameraOffset: Vector3 | null = null;
  logger.camera.debug(`All cameras in scene: ${scene.cameras.map(c => c.name).join(', ')}`);
  logger.camera.debug(`Spawn point: ${_spawnPoint ? `(${_spawnPoint.x.toFixed(2)}, ${_spawnPoint.y.toFixed(2)}, ${_spawnPoint.z.toFixed(2)})` : 'null'}`);

  const getCameraWorldPosition = (cam: Camera): Vector3 => {
    try {
      cam.computeWorldMatrix();
    } catch {
      // ignore
    }
    const anyCam = cam as any;
    if (typeof anyCam.getAbsolutePosition === 'function') return anyCam.getAbsolutePosition();
    if (anyCam.globalPosition) return anyCam.globalPosition as Vector3;
    return cam.position;
  };

  const getCameraTarget = (cam: Camera): Vector3 | null => {
    const anyCam = cam as any;
    if (typeof anyCam.getTarget === 'function') return anyCam.getTarget();
    return anyCam.target ?? null;
  };
  
  // Filter out our game cameras
  const sceneCameras = scene.cameras.filter(cam => 
    cam !== camera && 
    cam !== freeRoamCamera && 
    cam.name !== 'hud_material_preview_camera'
  );
  
  logger.camera.debug(`Scene cameras (filtered): ${sceneCameras.map(c => c.name).join(', ')}`);
  
  // Find the start camera - prefer mapConfig.startCamera, else first scene camera
  let startCamera: Camera | null = null;
  const startCameraName = mapConfig?.startCamera;
  
  if (startCameraName) {
    startCamera = sceneCameras.find(c => c.name === startCameraName || c.name.startsWith(startCameraName)) ?? null;
    if (!startCamera) {
      logger.camera.warn(`Start camera "${startCameraName}" not found in scene, using first available`);
    }
  }
  if (!startCamera && sceneCameras.length > 0) {
    startCamera = sceneCameras[0];
  }
  
  // Check if start camera has an animation
  let startCameraAnimation: import('@babylonjs/core').AnimationGroup | null = null;
  if (startCamera && cameraAnimations.length > 0) {
    logger.camera.log(`🎬 Looking for animation targeting camera "${startCamera.name}"...`);
    // Look for animation that targets this camera
    for (const animGroup of cameraAnimations) {
      logger.camera.log(`🎬 Checking animation group "${animGroup.name}" with ${animGroup.targetedAnimations.length} targeted animations`);
      for (const ta of animGroup.targetedAnimations) {
        const target = ta.target;
        const targetName = target && typeof target === 'object' && 'name' in target ? target.name : String(target);
        logger.camera.log(`🎬   - Target: "${targetName}", property: "${ta.animation.targetProperty}"`);
      }
      const targetsThisCamera = animGroup.targetedAnimations.some(ta => {
        const target = ta.target;
        return target === startCamera || 
               (target && typeof target === 'object' && 'name' in target && target.name === startCamera!.name);
      });
      if (targetsThisCamera) {
        startCameraAnimation = animGroup;
        logger.camera.log(`🎬 Found intro animation "${animGroup.name}" for camera "${startCamera.name}"`);
        break;
      }
    }
    if (!startCameraAnimation) {
      logger.camera.warn(`🎬 No animation found targeting camera "${startCamera.name}"`);
    }
  }
  
  // Store intro animation config for playback AFTER all assets are loaded
  // The animation will be played after loading complete, before handing control to player
  const introAnimationConfig = startCamera && startCameraAnimation && _spawnPoint ? {
    camera: startCamera,
    animation: startCameraAnimation,
    spawnPoint: _spawnPoint,
  } : null;
  
  if (introAnimationConfig && _spawnPoint) {
    const animDuration = (introAnimationConfig.animation.to - introAnimationConfig.animation.from) / 60;
    logger.camera.log(`🎬 Intro animation "${introAnimationConfig.animation.name}" (${animDuration.toFixed(2)}s) will play after loading complete`);
    
    // For animated camera, use FIRST frame position for initial camera offset
    // (final position will be captured after animation plays)
    const camPos = getCameraWorldPosition(introAnimationConfig.camera);
    derivedCameraOffset = camPos.clone().subtract(_spawnPoint);
    logger.camera.log(`📷 Initial camera position (will animate): (${camPos.x.toFixed(2)}, ${camPos.y.toFixed(2)}, ${camPos.z.toFixed(2)})`);
  } else if (startCamera && _spawnPoint) {
    // Static camera - use its position directly (existing behavior)
    const camPos = getCameraWorldPosition(startCamera);
    const anyCam = startCamera as any;
    const parentName = anyCam.parent?.name ?? null;
    logger.camera.debug(
      `Using camera '${startCamera.name}': local=${startCamera.position.toString()}, world=${camPos.toString()}, parent=${parentName}`
    );
    derivedCameraOffset = camPos.clone().subtract(_spawnPoint);
    logger.camera.log(`Derived camera offset from scene camera '${startCamera.name}': (${derivedCameraOffset.x.toFixed(2)}, ${derivedCameraOffset.y.toFixed(2)}, ${derivedCameraOffset.z.toFixed(2)})`);
  } else {
    logger.camera.warn(`Cannot derive camera offset - startCamera=${!!startCamera}, _spawnPoint=${!!_spawnPoint}`);
  }

  // Visualize map cameras in debug renderer
  const debugRenderer = DebugRenderer.getInstance(scene);
  for (const cam of sceneCameras) {
    const camPos = getCameraWorldPosition(cam);
    const target = getCameraTarget(cam);
    debugRenderer.createCameraVisualization(camPos, target, cam.name, new Color3(0, 1, 1));
    logger.camera.log(`🎥 Visualized camera '${cam.name}' at (${camPos.x.toFixed(2)}, ${camPos.y.toFixed(2)}, ${camPos.z.toFixed(2)})`);
  }

  // Apply map-level config overrides
  const gravitySetting = mapConfig?.physics?.gravity;
  if (typeof gravitySetting === 'number' && Number.isFinite(gravitySetting)) {
    physicsManager.setGravity(new Vector3(0, -Math.abs(gravitySetting), 0));
    logger.physics.log(`Map config: physics.gravity=${gravitySetting} (applied)`);
  } else if (gravitySetting instanceof Vector3) {
    physicsManager.setGravity(gravitySetting);
    logger.physics.log(`Map config: physics.gravity=(${gravitySetting.x}, ${gravitySetting.y}, ${gravitySetting.z}) (applied)`);
  }

  // Load environment settings from map config
  environmentSettings.loadFromMapConfig(mapConfig);
  
  // Initialize lens effect from map config
  if (mapConfig?.lens?.enabled && postProcessManager) {
    postProcessManager.initializeLensEffect({
      edge_blur: mapConfig.lens.edge_blur,
      chromatic_aberration: mapConfig.lens.chromatic_aberration,
      distortion: mapConfig.lens.distortion,
      grain_amount: mapConfig.lens.grain_amount,
      dof_focus_distance: mapConfig.lens.dof_focus_distance,
      dof_enabled: mapConfig.lens.dof_enabled,
      dof_aperture: mapConfig.lens.dof_aperture,
      dof_darken: mapConfig.lens.dof_darken,
      dof_pentagon: mapConfig.lens.dof_pentagon,
      dof_gain: mapConfig.lens.dof_gain,
      dof_threshold: mapConfig.lens.dof_threshold,
    });
    logger.level.log(`🔭 Lens effect initialized from map config`);
  }
  
  // Set up auto-focus target (player position)
  postProcessManager.setAutoFocusTarget(() => {
    return playerController?.character?.position ?? null;
  });

  // Emit environment info to debug UI
  debugUiBridge.emit({ type: 'environment:info', info: buildEnvironmentInfo() });

    // Update HUD if custom spline was loaded
    activeLoadedSpline = loadedSpline ?? null;
    // (splineSource is emitted to the HUD)
    if (splineDebugMesh) {
    splineDebugMesh.dispose();
    splineDebugMesh = null;
    }
    debugUiBridge.emit({ type: 'spline:status', found: !!loadedSpline, source: splineSource });

  if (loadedSpline) {
    logger.spline.log(`Loaded custom spline from map (Source: ${splineSource})`);
    splineConstraint.setSpline(loadedSpline);
    
    // Update camera controller to use new spline
    cameraController.clearSegments();
    
    // Apply default camera config from map if available, or derived from scene camera, otherwise use hard-coded fallback
    const defaultCamera = environmentSettings.getDefaultCameraConfig();

    // NOTE: follow-side expects tangent-space offset:
    // X=up/down (world Y), Y=forward/back along spline, Z=left/right.
    // Imported map camera offsets are derived in world-space, so convert them.
    let cameraOffset: Vector3;
    if (derivedCameraOffset) {
      if (_spawnPoint) {
        const frame = computeTangentFrame(loadedSpline, _spawnPoint);
        cameraOffset = worldToTangent(frame, derivedCameraOffset);
        logger.camera.log(
          `📷 Converted derived world offset -> tangent offset: world=(${derivedCameraOffset.x.toFixed(2)}, ${derivedCameraOffset.y.toFixed(2)}, ${derivedCameraOffset.z.toFixed(2)}) ` +
          `tangent=(upX=${cameraOffset.x.toFixed(2)}, fwdY=${cameraOffset.y.toFixed(2)}, rightZ=${cameraOffset.z.toFixed(2)})`
        );
      } else {
        // Should be unreachable (derivedCameraOffset requires spawn), but keep a safe fallback.
        cameraOffset = derivedCameraOffset;
      }
    } else {
      cameraOffset =
        defaultCamera?.type === 'follow' && defaultCamera.follow
          ? defaultCamera.follow.offset
          : new Vector3(1, 0, -19);
    }

    const cameraSmoothing = defaultCamera?.type === 'follow' && defaultCamera.follow ? 
      defaultCamera.follow.smoothing : 0.1;
    
    cameraController.addSegment({
      id: 'main',
      startT: 0.0,
      endT: 1.0,
      type: 'follow-side',
      data: {
        offset: cameraOffset,
        distance: -14,  // Legacy, not used with new system
        height: 1,      // Legacy, not used with new system
        smoothing: cameraSmoothing,
        spline: loadedSpline
      },
      transitionDuration: 1.0,
      transitionEasing: 'ease-in-out'
    });
    
    logger.camera.log(`📷 Camera initialized: offset=(${cameraOffset.x}, ${cameraOffset.y}, ${cameraOffset.z}), smoothing=${cameraSmoothing}`);
  } else {
    logger.spline.warn('No custom spline found, using default');
  }

  // Register NPC spawns from level data (AFTER spline is loaded so projection works correctly)
  if (npcSpawns && npcSpawns.length > 0) {
    logger.npc.log(`📍 Registering ${npcSpawns.length} NPC spawn(s) from map`);
    for (const spawn of npcSpawns) {
      npcSpawnManager.registerSpawn({
        id: spawn.id,
        characterId: spawn.characterId,
        position: spawn.position,
        spawnT: -1, // Will be calculated from position
        spawnRadius: spawn.spawnRadius,
        patrolLeftDistance: spawn.patrolLeftDistance,
        patrolRightDistance: spawn.patrolRightDistance,
        patrolWaitTime: spawn.patrolWaitTime,
        maxHealth: spawn.maxHealth, // Override from map authoring
        behaviorConfig: spawn.behaviorConfig,
      });
    }
  }

  // Preload all character assets for instant switching
  await characterCache.preload(danielConfig);
  loadingManager.reportLoaded('daniel');
  await characterCache.preload(lillyConfig);
  loadingManager.reportLoaded('lilly');
  await characterCache.preload(hotdogConfig);
  loadingManager.reportLoaded('hotdog');
  await characterCache.preload(lavazombieConfig);
  loadingManager.reportLoaded('lavazombie');
  logger.assets.log('All characters preloaded');

  // Start render loop BEFORE playing intro animation
  // This ensures animations can advance frames during cinematic playback
  sceneManager.startRenderLoop();
  sceneManager.handleResize();
  logger.core.log('Render loop started (before intro animation)');

  // === SPAWN PLAYER BEFORE INTRO (so player is visible during cinematic) ===
  // Load player using cached asset - do this BEFORE intro animation
  playerController.loadFromAsset(characterCache.get(currentLeaderId));
  logger.character.log('Player loaded (before intro animation)');
  
  // Ensure player idle animation is playing (AnimationController starts it, but force an update)
  const playerCharacter = playerController.character as any;
  if (playerCharacter.animationController) {
    playerCharacter.animationController.setState('idle', 0);
  }

  // Set spawn position early so player is visible during intro
  let playerInitialT = 0.15;
  let playerStartPos = splineConstraint.getPointAt(playerInitialT);
  let spawnHeight = 1.0; // Default height above ground
  
  // If we have a spawn point from the map, use it
  if (_spawnPoint) {
    spawnHeight = _spawnPoint.y;
    
    // If there's also a spline, project the spawn point onto the path (XZ plane only, preserve Y)
    if (loadedSpline) {
      // Start search from beginning of path to avoid wrapping to wrong side
      const projectionResult = splineConstraint.project(_spawnPoint, 0);
      playerInitialT = projectionResult.projection.t;
      playerStartPos = projectionResult.projection.splinePoint.clone();
      // Preserve the spawn point's Y coordinate (could be elevated platform, etc.)
      playerStartPos.y = spawnHeight;
      const distance = Vector3.Distance(_spawnPoint, projectionResult.projection.splinePoint);
      logger.level.log(`Spawn: (${_spawnPoint.x.toFixed(1)}, ${_spawnPoint.y.toFixed(1)}, ${_spawnPoint.z.toFixed(1)}) → Path T=${playerInitialT.toFixed(3)} (dist: ${distance.toFixed(2)}m)`);
      logger.player.log(`Spawn point projected to path at T: ${playerInitialT.toFixed(3)}, distance: ${distance.toFixed(2)}`);
    } else {
      // No spline, use spawn point position directly
      playerStartPos = _spawnPoint.clone();
      logger.level.log(`Spawn: (${_spawnPoint.x.toFixed(1)}, ${_spawnPoint.y.toFixed(1)}, ${_spawnPoint.z.toFixed(1)})`);
      logger.player.log(`Using spawn point position`);
    }
  } else {
    // No spawn point, use default T on spline
    playerStartPos.y += spawnHeight;
    logger.level.log(`Default spawn at T=${playerInitialT.toFixed(3)}`);
  }
  
  playerController.playerT = playerInitialT;
  playerController.character.position.copyFrom(playerStartPos);
  playerController.resetPhysics();
  logger.player.log(`Player spawned at T: ${playerInitialT.toFixed(3)}, pos: (${playerStartPos.x.toFixed(2)}, ${playerStartPos.y.toFixed(2)}, ${playerStartPos.z.toFixed(2)})`);

  // Setup party - spawn NPCs before intro so they're visible during cinematic
  partyManager.setLeader(playerController);
  
  // Spawn NPCs behind player using PartyManager
  for (let i = 1; i < partyMembers.length; i++) {
    const member = partyMembers[i];
    const npcInitialT = Math.max(0, playerInitialT - (i * 0.05));
    
    // Set follow AI - follow player or previous NPC
    const targetFunc = i === 1 ? () => playerController.playerT : () => partyNPCs[i - 2].playerT;
    
    const npc = await partyManager.addFollower(
      targetFunc,
      {
        id: `${member.id}_npc_${i}`,
        characterConfig: member.config,
        initialT: npcInitialT,
        minDistance: FOLLOW_MIN_DISTANCE_METERS,  // Minimum distance in meters before NPC stops following
        maxDistance: FOLLOW_MAX_DISTANCE_METERS   // Maximum distance in meters before NPC starts following
      },
      characterCache.get(member.id)
    );

    // Prepare followers so they can be possessed later without recreation
    npc.enablePlayerControl(input, cameraController);
    npc.setCombatSystem(combatSystem);

    // Followers should not collide with other characters (only static/dynamic world)
    npc.setCollidesWithCharacters(false);
    
    // Disable collisions
    npc.character.checkCollisions = false;
    npc.character.getChildMeshes().forEach(mesh => {
      mesh.checkCollisions = false;
    });
    
    partyNPCs.push(npc);
    partyCharacters[i] = npc;
    logger.npc.log(`Spawned party member ${i}: ${member.id} (before intro animation)`);
    
    // Ensure NPC idle animation is playing
    const npcCharacter = npc.character as any;
    if (npcCharacter.animationController) {
      npcCharacter.animationController.setState('idle', 0);
    }
  }

  // Register character update loop BEFORE intro animation so characters can fall/animate during cinematic
  // This is a temporary observer that handles physics and animation until the main loop takes over
  const earlyCharacterUpdateObserver = scene.onBeforeRenderObservable.add(() => {
    const rawDeltaTime = engine.getDeltaTime() / 1000;
    const deltaTime = gameTime.scale(rawDeltaTime);
    
    // Update physics manager for character collisions
    physicsManager.update(deltaTime);
    
    // Update all party characters (player + NPCs) - handles gravity, ground collision, animation
    for (const ch of partyCharacters) {
      if (ch) {
        ch.update(deltaTime);
      }
    }
  });

  // === PLAY INTRO CAMERA ANIMATION (after all loading complete) ===
  // This ensures the loading UI is hidden before the cinematic plays
  if (introAnimationConfig) {
    const { camera: introCamera, animation: introAnimation, spawnPoint: _introSpawnPoint } = introAnimationConfig;
    const animDuration = (introAnimation.to - introAnimation.from) / 60; // Assuming 60fps
    
    if (animDuration > 0) {
      logger.camera.log(`🎬 Playing intro camera animation "${introAnimation.name}" (${animDuration.toFixed(2)}s, frames ${introAnimation.from}-${introAnimation.to})...`);
      
      // Copy clipping planes from main camera to ensure proper rendering
      introCamera.minZ = camera.minZ;
      introCamera.maxZ = camera.maxZ;
      
      // Debug camera state before playback
      const anyCam = introCamera as any;
      logger.camera.log(`🎬 Camera class: ${introCamera.getClassName()}, local position: ${introCamera.position.toString()}`);
      logger.camera.log(`🎬 Camera parent: ${introCamera.parent?.name ?? 'null'}`);
      const worldPos = getCameraWorldPosition(introCamera);
      logger.camera.log(`🎬 Camera world position: ${worldPos.toString()}`);
      
      // Check if animation targets a parent TransformNode instead of the camera
      // glTF imports create a parent TransformNode for cameras, and animations target that
      const animationTarget = introAnimation.targetedAnimations[0]?.target;
      const animationTargetsParent = animationTarget && 
        animationTarget !== introCamera && 
        animationTarget.name === introCamera.name;
      
      if (animationTargetsParent && introCamera.parent) {
        // The animation targets the parent TransformNode, not the camera
        // Keep the camera parented - it will inherit the animated transform
        const parentNode = introCamera.parent as import('@babylonjs/core').TransformNode;
        logger.camera.log(`🎬 Animation targets parent TransformNode "${parentNode.name}" - camera will inherit animated transform`);
        
        // Verify parent is being animated
        parentNode.computeWorldMatrix(true);
        const parentWorldPos = parentNode.getAbsolutePosition();
        logger.camera.log(`🎬 Parent world position: (${parentWorldPos.x.toFixed(2)}, ${parentWorldPos.y.toFixed(2)}, ${parentWorldPos.z.toFixed(2)})`);
      }
      
      // Ensure camera uses rotationQuaternion for animation (glTF animations target rotationQuaternion)
      if (!anyCam.rotationQuaternion) {
        const { Quaternion } = await import('@babylonjs/core');
        anyCam.rotationQuaternion = Quaternion.FromEulerVector(anyCam.rotation || Vector3.Zero());
        logger.camera.log(`🎬 Initialized rotationQuaternion from euler rotation`);
      }
      
      logger.camera.log(`🎬 Camera rotation: ${anyCam.rotation?.toString() ?? 'null'}, rotationQuaternion: ${anyCam.rotationQuaternion?.toString() ?? 'null'}`);
      if (typeof anyCam.getTarget === 'function') {
        logger.camera.log(`🎬 Camera target: ${anyCam.getTarget().toString()}`);
      }
      
      // Note: We don't call goToFrame(0) here because the animation data may be in a different
      // coordinate space. The animation.start() will apply the animation from its beginning.
      
      // Disable player input during intro
      input.setMovementEnabled(false);
      
      // Enter cinematic mode (camera controlled by animation)
      cameraController.enterCinematicMode();
      
      // Instead of using the glTF camera (which has view matrix issues with parent transforms),
      // we'll create a temporary FreeCamera for the cinematic animation.
      // The main game camera is an ArcRotateCamera, which computes position from target+angles+radius,
      // making it impossible to directly set position. A FreeCamera allows direct position control.
      
      // Get the animation target - this is the TransformNode being animated
      const animTarget = introAnimation.targetedAnimations[0]?.target as import('@babylonjs/core').TransformNode | undefined;
      
      if (!animTarget) {
        logger.camera.warn(`🎬 No animation target found, skipping intro animation`);
      } else {
        // Debug: Log all animation targets
        logger.camera.log(`🎬 Animation group "${introAnimation.name}" has ${introAnimation.targetedAnimations.length} targeted animations:`);
        for (const ta of introAnimation.targetedAnimations) {
          logger.camera.log(`🎬   - ${ta.animation.targetProperty} on "${ta.target?.name}" (id: ${ta.target?.uniqueId})`);
        }
        
        // Debug: Log parent hierarchy of animation target
        let parentChain = '';
        let node: import('@babylonjs/core').Node | null = animTarget;
        while (node) {
          const scale = (node as import('@babylonjs/core').TransformNode).scaling;
          parentChain += `${node.name}(scale: ${scale?.x?.toFixed(2) ?? '?'}, ${scale?.y?.toFixed(2) ?? '?'}, ${scale?.z?.toFixed(2) ?? '?'}) -> `;
          node = node.parent;
        }
        logger.camera.log(`🎬 Animation target parent chain: ${parentChain}null`);
        
        // Create a temporary FreeCamera for the cinematic
        // ArcRotateCamera can't have its position directly set (it computes from target+angles)
        const cinematicCamera = new FreeCamera('cinematicCamera', animTarget.position.clone(), scene);
        cinematicCamera.minZ = camera.minZ;
        cinematicCamera.maxZ = camera.maxZ;
        cinematicCamera.layerMask = camera.layerMask;
        
        // Disable any camera inertia/smoothing for precise animation control
        cinematicCamera.inertia = 0;
        cinematicCamera.angularSensibility = 0; // Disable mouse control
        
        // Use activeCameras (array) since that's what the game uses - it takes precedence over activeCamera
        scene.activeCameras = [cinematicCamera];
        
        logger.camera.log(`🎬 Created cinematic FreeCamera, syncing to animated node "${animTarget.name}"`);
        
        // Import Quaternion for rotation handling
        const { Quaternion } = await import('@babylonjs/core');
        
        // Ensure cinematic camera uses quaternion for rotation
        cinematicCamera.rotationQuaternion = Quaternion.Identity();
        
        // Register observer to sync camera position each frame
        let syncObserver: import('@babylonjs/core').Nullable<import('@babylonjs/core').Observer<import('@babylonjs/core').Scene>> = null;
        
        let syncCallCount = 0;
        syncObserver = scene.onBeforeRenderObservable.add(() => {
          if (animTarget) {
            syncCallCount++;
            
            // Compute the world matrix to get absolute transform (includes parent's -X scale)
            animTarget.computeWorldMatrix(true);
            const worldPos = animTarget.getAbsolutePosition();
            
            // Log every 60 frames (~1 second at 60fps)
            if (syncCallCount % 60 === 1) {
              const localRot = animTarget.rotationQuaternion;
              logger.camera.log(`🎬 [SYNC] frame ${syncCallCount}: world pos: (${worldPos.x.toFixed(2)}, ${worldPos.y.toFixed(2)}, ${worldPos.z.toFixed(2)}), rot: (${localRot?.x.toFixed(3) ?? 'null'}, ${localRot?.y.toFixed(3) ?? 'null'}, ${localRot?.z.toFixed(3) ?? 'null'}, ${localRot?.w.toFixed(3) ?? 'null'})`);
            }
            
            // Copy world position directly - parent transform already applied
            cinematicCamera.position.copyFrom(worldPos);
            
            // For rotation under -X scale (reflection across YZ plane):
            // The correct transformation is the quaternion conjugate: negate X, Y, Z; keep W
            // This properly reflects the rotation across the mirror plane
            const localRot = animTarget.rotationQuaternion;
            
            if (cinematicCamera.rotationQuaternion && localRot) {
              // Conjugate for -X reflection: (x, y, z, w) -> (-x, -y, -z, w)
              cinematicCamera.rotationQuaternion.set(-localRot.x, -localRot.y, -localRot.z, localRot.w);
              
              // Debug: log rotation
              if (syncCallCount % 60 === 1) {
                logger.camera.log(`🎬 [ROT] local: (${localRot.x.toFixed(3)}, ${localRot.y.toFixed(3)}, ${localRot.z.toFixed(3)}, ${localRot.w.toFixed(3)}) -> applied: (${cinematicCamera.rotationQuaternion.x.toFixed(3)}, ${cinematicCamera.rotationQuaternion.y.toFixed(3)}, ${cinematicCamera.rotationQuaternion.z.toFixed(3)}, ${cinematicCamera.rotationQuaternion.w.toFixed(3)})`);
              }
            }
          }
        });
        
        // Play the animation and wait for completion with timeout fallback
        await new Promise<void>((resolve) => {
        const maxWaitMs = Math.max(animDuration * 1000 + 2000, 5000); // Animation duration + 2s buffer, min 5s
        let resolved = false;
        let debugInterval: ReturnType<typeof setInterval> | null = null;
        
        const timeout = setTimeout(() => {
          if (!resolved) {
            resolved = true;
            if (debugInterval) clearInterval(debugInterval);
            logger.camera.warn(`🎬 Animation timeout after ${maxWaitMs}ms, continuing...`);
            resolve();
          }
        }, maxWaitMs);
        
        introAnimation.onAnimationGroupEndObservable.addOnce(() => {
          if (!resolved) {
            resolved = true;
            clearTimeout(timeout);
            if (debugInterval) clearInterval(debugInterval);
            logger.camera.log(`🎬 Animation ended via observable`);
            resolve();
          }
        });
        
        // Debug: Log position and frame every second during animation
        debugInterval = setInterval(() => {
          const animatables = introAnimation.animatables;
          const masterFrame = animatables.length > 0 ? animatables[0].masterFrame : -1;
          const isPlaying = introAnimation.isPlaying;
          if (animTarget && animTarget.position) {
            const pos = animTarget.position;
            logger.camera.log(`🎬 [ANIM] frame=${masterFrame.toFixed(1)}, playing=${isPlaying}, Target pos: (${pos.x.toFixed(2)}, ${pos.y.toFixed(2)}, ${pos.z.toFixed(2)}), Camera pos: (${camera.position.x.toFixed(2)}, ${camera.position.y.toFixed(2)}, ${camera.position.z.toFixed(2)})`);
          } else {
            logger.camera.log(`🎬 [ANIM] frame=${masterFrame.toFixed(1)}, playing=${isPlaying}, animatables=${animatables.length}`);
          }
        }, 1000);
        
        // Log animatables info before starting
        logger.camera.log(`🎬 Animation animatables before start: ${introAnimation.animatables.length}`);
        
        // Ensure all individual animations are set to NOT loop
        for (const ta of introAnimation.targetedAnimations) {
          // AnimationGroup start(loop=false) should handle this, but be explicit
          // Animation.ANIMATIONLOOPMODE_CONSTANT = 0 (play once)
          ta.animation.loopMode = 0;
        }
        
        introAnimation.start(false, 1.0);
        
        // Log animatables info after starting
        logger.camera.log(`🎬 Animation animatables after start: ${introAnimation.animatables.length}`);
        logger.camera.log(`🎬 Animation started, isPlaying=${introAnimation.isPlaying}, speedRatio=${introAnimation.speedRatio}`);
        logger.camera.log(`🎬 Animation from=${introAnimation.from}, to=${introAnimation.to}`);
      });
      
      // Remove sync observer now that animation is complete
      if (syncObserver) {
        scene.onBeforeRenderObservable.remove(syncObserver);
        logger.camera.log(`🎬 Removed camera sync observer`);
      }
      
      logger.camera.log(`🎬 Intro animation complete, capturing final camera position`);
      
      // Capture final camera position from the cinematic camera
      const finalCamPos = cinematicCamera.position.clone();
      
      // Use CURRENT player position (player may have fallen/moved during cinematic)
      const currentPlayerPos = playerController.character.position.clone();
      const finalOffset = finalCamPos.clone().subtract(currentPlayerPos);
      logger.camera.log(`📷 Final camera position: (${finalCamPos.x.toFixed(2)}, ${finalCamPos.y.toFixed(2)}, ${finalCamPos.z.toFixed(2)})`);
      logger.camera.log(`📷 Current player position: (${currentPlayerPos.x.toFixed(2)}, ${currentPlayerPos.y.toFixed(2)}, ${currentPlayerPos.z.toFixed(2)})`);
      logger.camera.log(`📷 Final derived offset: (${finalOffset.x.toFixed(2)}, ${finalOffset.y.toFixed(2)}, ${finalOffset.z.toFixed(2)})`);
      
      // Update camera controller with final offset computed at CURRENT player position
      // This ensures the follow camera starts at exactly the cinematic camera's final position
      if (loadedSpline) {
        const frame = computeTangentFrame(loadedSpline, currentPlayerPos);
        const tangentOffset = worldToTangent(frame, finalOffset);
        cameraController.updateSegmentOffset('main', tangentOffset);
        logger.camera.log(`📷 Updated camera segment with final tangent offset: (${tangentOffset.x.toFixed(2)}, ${tangentOffset.y.toFixed(2)}, ${tangentOffset.z.toFixed(2)})`);
      }
      
      // Capture final camera state BEFORE disposing
      const finalCinematicPos = cinematicCamera.position.clone();
      
      // Calculate where the cinematic camera was actually looking (from its rotation)
      // FreeCamera looks in local +Z direction in Babylon.js
      const forwardLocal = new Vector3(0, 0, 1);
      const forwardWorld = forwardLocal.clone();
      if (cinematicCamera.rotationQuaternion) {
        forwardWorld.rotateByQuaternionToRef(cinematicCamera.rotationQuaternion, forwardWorld);
      }
      // Target is a point along the forward direction (at a reasonable distance)
      const finalCinematicTarget = finalCinematicPos.add(forwardWorld.scale(10));
      
      logger.camera.log(`📷 Final cinematic: pos=(${finalCinematicPos.x.toFixed(2)}, ${finalCinematicPos.y.toFixed(2)}, ${finalCinematicPos.z.toFixed(2)})`);
      logger.camera.log(`📷 Cinematic was looking at: (${finalCinematicTarget.x.toFixed(2)}, ${finalCinematicTarget.y.toFixed(2)}, ${finalCinematicTarget.z.toFixed(2)})`);
      logger.camera.log(`📷 Will blend rotation toward player at: (${currentPlayerPos.x.toFixed(2)}, ${currentPlayerPos.y.toFixed(2)}, ${currentPlayerPos.z.toFixed(2)})`);
      
      // Switch back to the main game camera
      scene.activeCameras = [camera];
      
      // Dispose the temporary cinematic camera
      cinematicCamera.dispose();
      logger.camera.log(`🎬 Disposed cinematic camera, switched back to main camera`);
      
      // Exit cinematic mode with blend - pass final cinematic camera position for smooth transition
      cameraController.exitCinematicMode(1.5, finalCinematicPos, finalCinematicTarget);
      
      // Re-enable input
      input.setMovementEnabled(true);
      } // End of else block for animTarget check
    } else {
      logger.camera.warn(`🎬 Animation has no valid frames (from=${introAnimation.from}, to=${introAnimation.to}), skipping playback`);
    }
  }

  // Remove early character update observer - main loop will take over
  if (earlyCharacterUpdateObserver) {
    scene.onBeforeRenderObservable.remove(earlyCharacterUpdateObserver);
    logger.core.log('Early character update observer removed, main loop taking over');
  }

  // Track base updateSpeed for particle systems to allow proper time scaling
  const particleBaseUpdateSpeeds = new WeakMap<object, number>();
  
  scene.onBeforeRenderObservable.add(() => {
    const rawDeltaTime = engine.getDeltaTime() / 1000; // Convert to seconds
    
    // Update game time system (handles time ramping)
    gameTime.update(rawDeltaTime);
    
    // Sync Babylon.js animation system with our game time
    scene.animationTimeScale = gameTime.timeScale;
    
    // Sync all particle systems with game time (slow-mo, etc.)
    // Store base speed on first encounter, then scale relative to it
    for (const ps of scene.particleSystems) {
      if (!particleBaseUpdateSpeeds.has(ps)) {
        particleBaseUpdateSpeeds.set(ps, ps.updateSpeed);
      }
      const baseSpeed = particleBaseUpdateSpeeds.get(ps) ?? 1;
      ps.updateSpeed = baseSpeed * gameTime.timeScale;
    }
    
    // Get scaled delta time from gameTime system
    const deltaTime = gameTime.scale(rawDeltaTime);
    
    // Set physics time step for debris and other physics-simulated objects
    physicsManager.setTimeStep(deltaTime);
    
    // Update physics manager (collision polling, performance stats, global hooks)
    physicsManager.update(deltaTime);
    
    // Update spatial culling based on player position
    const spatialCulling = ServiceRegistry.getSpatialCulling();
    if (spatialCulling && playerController) {
      spatialCulling.update(playerController.character.position);
    }
    
    // Update entities
    entityRegistry.update(deltaTime);
    
    // Update party characters (avoid PartyManager.update to prevent double-updating on leader swaps)
    for (let i = 0; i < partyCharacters.length; i++) {
      const ch = partyCharacters[i];
      if (!ch) {
        continue;
      }

      ch.update(deltaTime);

      if (ch.consumeDespawnRequest()) {
        logger.character.info(`Despawning character ${ch.getId()}`);

        if (partyNPCs.includes(ch)) {
          partyManager.removeFollower(ch);
          const npcIndex = partyNPCs.indexOf(ch);
          if (npcIndex >= 0) {
            partyNPCs.splice(npcIndex, 1);
          }
        }

        ch.dispose();
        partyCharacters[i] = null;

        if (partyLeaderIndex === i) {
          const nextLeader = partyCharacters.findIndex((entry) => entry !== null);
          partyLeaderIndex = nextLeader >= 0 ? nextLeader : 0;
        }
      }
    }

    partyManager.updateLeaderMarker();

    // Update world NPCs (proximity spawning/despawning and AI)
    if (playerController) {
      npcSpawnManager.update(playerController.character.position, playerController.playerT, deltaTime);
    }

    // Combine party characters with world NPCs for combat and damage systems
    const partyActiveCharacters = partyCharacters.filter((c): c is NPCController => !!c);
    const worldNPCs = npcSpawnManager.getActiveNPCs();
    const activeCharacters = [...partyActiveCharacters, ...worldNPCs];
    
    damageReactionSystem.setCharacters(activeCharacters);
    damageReactionSystem.update(deltaTime);

    // Update combat system (after character updates)
    combatSystem.update(deltaTime, activeCharacters);
    
    // Render combat debug visualizations (hitboxes, character capsules)
    if (combatDebugEnabled) {
      combatSystem.renderDebug(activeCharacters);
    }
    
    // Emit combat debug state for UI
    debugUiBridge.emit({
      type: 'combat-debug-state',
      weapons: combatSystem.getDebugWeaponInfo(), // Serializable data
      projectiles: combatSystem.getDebugProjectileInfo(),
      recentDamage: [], // TODO: Track recent damage events in CombatSystem
      lastHit: combatSystem.getLastHitInfo(),
    });
    
    // Update triggers - use actual character ID, not 'player' literal
    const playerId = playerController.getId();
    triggerSystem.updateSingle(playerId, playerController.character.position, deltaTime);
    triggerSystem.updatePlayerDebugPosition(playerController.character.position);
    
    // Update light debug meshes to follow moving lights and sync with spawned/despawned lights
    if (lightDebugEnabled) {
      // Get current lights with positions
      const currentLights = scene.lights.filter(l => 'position' in l && (l as any).position);
      const currentLightNames = new Set(currentLights.map(l => l.name));
      const existingGizmoLightNames = new Set(lightDebugMeshMap.values());
      
      // Remove gizmos for lights that no longer exist
      const meshesToRemove: typeof lightDebugMeshes = [];
      for (const debugMesh of lightDebugMeshes) {
        const lightName = lightDebugMeshMap.get(debugMesh.name);
        if (!lightName || !currentLightNames.has(lightName)) {
          debugMesh.dispose();
          meshesToRemove.push(debugMesh);
          if (lightName) lightDebugMeshMap.delete(debugMesh.name);
        }
      }
      lightDebugMeshes = lightDebugMeshes.filter(m => !meshesToRemove.includes(m));
      
      // Create gizmos for new lights
      for (const light of currentLights) {
        if (!existingGizmoLightNames.has(light.name)) {
          const pos = ((light as any).position as Vector3).clone();
          const sphere = MeshBuilder.CreateSphere(
            `debug_light_${light.name}`,
            { diameter: 0.5, segments: 8 },
            scene
          );
          sphere.position = pos;
          sphere.isPickable = false;
          sphere.checkCollisions = false;
          const mat = new StandardMaterial(`debug_light_mat_${light.name}`, scene);
          mat.emissiveColor = light.diffuse?.clone() || new Color3(1, 1, 1);
          mat.disableLighting = true;
          mat.alpha = light.isEnabled() ? 1.0 : 0.3;
          sphere.material = mat;
          sphere.layerMask = DEBUG_LAYER_MASK;
          lightDebugMeshes.push(sphere);
          lightDebugMeshMap.set(sphere.name, light.name);
        }
      }
      
      // Update positions and alpha for existing gizmos
      for (const debugMesh of lightDebugMeshes) {
        const lightName = lightDebugMeshMap.get(debugMesh.name);
        if (lightName) {
          const light = scene.getLightByName(lightName);
          if (light && 'position' in light && (light as any).position) {
            debugMesh.position.copyFrom((light as any).position);
            // Update alpha based on enabled state
            const mat = debugMesh.material as StandardMaterial;
            if (mat) mat.alpha = light.isEnabled() ? 1.0 : 0.3;
          }
        }
      }
    }
    
    // Update ServiceRegistry (disposables with update loops)
    ServiceRegistry.update(deltaTime);
    
    // Update unified light manager - reassigns pool lights based on player position
    UnifiedLightManager.update(playerController.character.position);
    
    // Update particle systems (follow-mode smoothing)
    particleManager.update(deltaTime);
    
    // Update FX effects (screen ripple, camera shake) - uses raw time for real-speed during slow-mo
    fxManager.update(rawDeltaTime);
    
    // Update camera controller
    if (cameraController) {
      cameraController.update(playerController.playerT, playerController.character.position, deltaTime);
    }

    // Animate pixelation size (replaces old GameUI animation)
    if (pixelationEnabled && animatePixelation) {
      pixelationTime += deltaTime;
      const baseSize = 16;
      const amplitude = 12;
      const speed = 2;
      const animatedSize = baseSize + Math.sin(pixelationTime * speed) * amplitude;
      postProcessManager.pixelateSizeX = animatedSize;
      postProcessManager.pixelateSizeY = animatedSize;
    }

    // Record animation timeline frame (unconditional; panel reads from the singleton store)
    const animState = playerController.animationController?.getDebugState();
    if (animState) {
      recordingManagerStore.recordFrame({
        characterId: currentLeaderId,
        debugState: animState,
        currentState: playerController.getCurrentState(),
        currentPhase: null,
      });
    }

    // Push a lightweight HUD snapshot each frame
    debugUiBridge.emitSnapshot({
      characterId: currentLeaderId,
      state: playerController.getCurrentState(),
      animationState: playerController.getCurrentAnimationState(),
      cameraMode: freeRoamEnabled ? 'free-roam' : 'follow',
      velocityY: playerController.velocity.y,
      position: {
        x: playerController.character.position.x,
        y: playerController.character.position.y,
        z: playerController.character.position.z,
      },
      grounded: playerController.isGrounded,
      animationDebugInfo: playerController.getAnimationDebugInfo(),
      missingAnimations: playerController.getMissingAnimations(),
      lights: {
        total: scene.lights.length,
        enabled: scene.lights.filter(l => l.isEnabled()).length,
        details: buildLightDebugInfo(),
      },
      fps: {
        current: engine.getFps(),
        frameTimeMs: engine.getDeltaTime(),
      },
      physics: physicsManager.getPerformanceStats(),
      culling: (() => {
        const culling = ServiceRegistry.getSpatialCulling();
        if (!culling) return undefined;
        const stats = culling.getStats();
        return {
          enabled: stats.enabled,
          cullDistance: stats.cullDistance,
          frustumFarPlane: stats.frustumFarPlane,
          totalMeshes: stats.totalMeshes,
          visibleMeshes: stats.visibleMeshes,
          culledMeshes: stats.culledMeshes,
          totalLights: stats.totalLights,
          visibleLights: stats.visibleLights,
          culledLights: stats.culledLights,
        };
      })(),
    });

    // Update mobile debug stats (every frame for smooth display)
    if (virtualGamepad) {
      virtualGamepad.updateStats({
        fps: engine.getFps(),
        frameTimeMs: engine.getDeltaTime(),
      });
    }

    // Update camera info periodically (every 5 frames to reduce overhead)
    if (scene.getFrameId() % 5 === 0) {
      debugUiBridge.emit({ type: 'environment:info', info: buildEnvironmentInfo() });
    }
  });

  // Ensure Babylon has finished any pending resource loads/compilations
  // (e.g., textures/materials from glTF) before the first frame.
  await scene.whenReadyAsync();
  logger.core.log('Scene assets ready');

  // Render loop already started earlier (before intro animation)
  // Just log initialization complete
  logger.userInfo('Hi Mum Game initialized! Controls: A/D = Move, Space = Jump');
}

  await initLevelAndPhysics();
}

// Mount loading overlay
const loadingOverlay = document.createElement('himum-loading-overlay');
document.body.appendChild(loadingOverlay);

initGame().catch(error => {
  logger.userError('Game initialization failed:', error);
});

// Debug input (only when input scope enabled)
window.addEventListener('keydown', (e) => {
  if (['a', 'd', 'w', ' ', 'arrowleft', 'arrowright', 'arrowup'].includes(e.key.toLowerCase())) {
    logger.input.debug('Key pressed:', e.key);
  }
});

// Free-roam camera toggle (F6) - engine needs direct access to camera state
window.addEventListener('keydown', (e) => {
  if (e.key === 'F6' && !e.repeat) {
    e.preventDefault();
    setFreeRoamEnabled(!freeRoamEnabled);
  }
});

// F7 is now handled via Debug HUD (toggleDebugRendering action)
// which enables all debug visuals including trigger zones

// Party leader switching (cycle only)
let isSwitching = false;
let prevCyclePartyPressed = false;

async function switchPartyLeader(targetIndex: number) {
  if (targetIndex >= partyMembers.length || targetIndex === partyLeaderIndex || isSwitching) {
    return;
  }
  
  isSwitching = true;
  logger.player.log(`Switching party leader from ${partyLeaderIndex} to ${targetIndex}`);

  try {
    const splineLength = splineConstraint.getTotalLength();
    const oldLeader = partyCharacters[partyLeaderIndex];
    const newLeader = partyCharacters[targetIndex];

    if (!oldLeader || !newLeader) {
      logger.player.warn('Party leader switch skipped: missing character instance', {
        partyLeaderIndex,
        targetIndex,
        hasOldLeader: !!oldLeader,
        hasNewLeader: !!newLeader
      });
      return;
    }

    // Swap control only: keep both characters in place.
    oldLeader.setPlayerControlled(false);
    newLeader.setPlayerControlled(true);

    // Update collision masks: leader collides with characters, followers do not
    newLeader.setCollidesWithCharacters(true);
    oldLeader.setCollidesWithCharacters(false);

    // Collisions: leader collides, followers don't (matches previous behavior)
    newLeader.character.checkCollisions = true;
    newLeader.character.getChildMeshes().forEach(mesh => {
      mesh.checkCollisions = false;
    });

    oldLeader.character.checkCollisions = false;
    oldLeader.character.getChildMeshes().forEach(mesh => {
      mesh.checkCollisions = false;
    });

    // Update follow chain (all non-leader members follow leader/previous follower)
    const followerIndices: number[] = [];
    for (let i = 0; i < partyCharacters.length; i++) {
      if (i !== targetIndex && partyCharacters[i]) followerIndices.push(i);
    }

    // Make sure the leader has no AI
    newLeader.setAIBehavior(null);

    for (let i = 0; i < followerIndices.length; i++) {
      const follower = partyCharacters[followerIndices[i]];
      if (!follower) continue;

      follower.setPlayerControlled(false);
      follower.setCollidesWithCharacters(false);
      follower.character.checkCollisions = false;
      follower.character.getChildMeshes().forEach(mesh => {
        mesh.checkCollisions = false;
      });

      const targetTFunc = i === 0
        ? () => newLeader.playerT
        : () => (partyCharacters[followerIndices[i - 1]] as NPCController).playerT;

      follower.setAIBehavior(
        new FollowAI(
          targetTFunc,
          FOLLOW_MAX_DISTANCE_METERS,
          splineLength,
          FOLLOW_MIN_DISTANCE_METERS
        )
      );
    }

    // Maintain partyNPCs list for any external code (followers only)
    partyNPCs.length = 0;
    for (const idx of followerIndices) {
      const follower = partyCharacters[idx];
      if (follower) partyNPCs.push(follower);
    }

    // Update references
    playerController = newLeader;
    partyLeaderIndex = targetIndex;
    currentLeaderId = partyMembers[targetIndex].id;

    partyManager.setLeader(newLeader);

    logger.player.log(`Switched to party member ${targetIndex + 1}: ${currentLeaderId}`);
  } catch (err) {
    logger.userError('Party leader switch failed:', err);
  } finally {
    isSwitching = false;
  }
}

// Mobile/virtual gamepad: cycle party leader button (single-trigger on press).
scene.onBeforeRenderObservable.add(() => {
  const pressed = input.cycleParty;
  if (pressed && !prevCyclePartyPressed) {
    if (partyMembers.length > 1) {
      const nextIndex = (partyLeaderIndex + 1) % partyMembers.length;
      // Fire-and-forget; internal guard prevents overlaps.
      void switchPartyLeader(nextIndex);
    }
  }
  prevCyclePartyPressed = pressed;
});
